/**
 * LabOrderModalCore.tsx
 *
 * Vendor-agnostic lab ordering form, extracted from the original
 * QuestLabOrderModal so it can back BOTH Quest and Vibrant ordering without
 * duplicating ~1.5k lines. Vendor differences (which sections show, which
 * services to call, how the submit response is parsed) are injected via a
 * `LabOrderConfig` adapter.
 *
 * Quest parity: QuestLabOrderModal now renders this core with a config that
 * enables every section and wires the exact same Quest service functions, so
 * Quest behavior is unchanged. Vibrant passes a reduced config (no
 * facility/provider, billing, diagnoses, or presets — its SFTP account has none
 * of those concepts) and its own test-search + submit.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { isBlankGender, toGenderCode } from '@/lib/gender';
import { createPortal } from 'react-dom';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X, Plus, Trash2, ChevronDown, ChevronRight, Search, CheckCircle, Save, Truck } from 'lucide-react';
import type {
  QuestInsurance,
  QuestTestCode,
  QuestDiagnosis,
  QuestOrderPayload,
  QuestPresetItem,
  QuestOrderNote,
  QuestFacility,
  QuestProvider,
} from '@/services/labs.service';
import { getErrorMessage } from '@/services/apiClient';
import { getPatientShippingAddresses, savePatientShippingAddress, type PatientShippingAddress } from '@/services/shippingAddresses.service';
import { usePatientDetail } from '@/hooks/usePatientDetail';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/Button';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import { Spinner } from '@/components/ui';
import { DatePicker } from '@/components/ui/DatePicker';
import { TimeField, formatTime12h } from '@/components/ui/TimeField';
import { nowHhmm } from '@/components/ui/CalendarDatePicker';
import { todayYmd } from '@/lib/utils';

const BUSINESS_UNITS = ['Default', 'WDL', 'PHP', 'NEL', 'DAL', 'DLO', 'STL', 'TMP', 'MET', 'PBL', 'SEA'];

const BILLING_TYPES = [
  { value: 'C', label: 'Client Bill' },
  { value: 'P', label: 'Patient Bill' },
  { value: 'T', label: 'Third Party' },
];

/** Normalized submit outcome so the core never needs vendor-specific parsing. */
/**
 * Whole years between a Y/M/D birth date and today, or null when the parts do
 * not form a real calendar date ("02/31", "13/01", a 2-digit year).
 */
export function ageFromDob(year: string, month: string, day: string, today: Date = new Date()): number | null {
  if (!/^\d{4}$/.test(year) || !/^\d{1,2}$/.test(month) || !/^\d{1,2}$/.test(day)) return null;
  const y = Number(year), m = Number(month), d = Number(day);
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  let age = today.getFullYear() - y;
  if (today.getMonth() < m - 1 || (today.getMonth() === m - 1 && today.getDate() < d)) age -= 1;
  return age;
}

export interface LabOrderSubmitResult {
  ok: boolean;
  /** Human-readable result lines shown on the success screen. */
  lines: string[];
  /** Set when ok=false; shown in the red banner and returns to the form. */
  errorMessage?: string;
  /**
   * With ok=false: the order was saved, so sending this form again would place
   * it twice. Send Order stays disabled until the form is closed. A vendor
   * that never sets it keeps Send Order enabled after a failure.
   */
  lockSend?: boolean;
}

/** Shape the preset loader returns. */
export interface ParsedLabPreset {
  facility?: string;
  /** Location picker vendors (Vibrant): the location the preset / order is for. */
  routingId?: string;
  provider?: string;
  patient?: {
    firstName: string;
    lastName: string;
    birthYear: string;
    birthMonth: string;
    birthDay: string;
    gender: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
  };
  pidNotes: QuestOrderNote[];
  insurance: Partial<InsuranceEntry>[];
  diagnoses: Array<{ code: string; title: string }>;
  /** `routingId`: the location a test's code belongs to ('' = every location; absent = unknown). */
  tests: Array<{ testCode: string; description: string; collectionDate: string; obrNotes: QuestOrderNote[]; routingId?: string }>;
}

/** A test search that may have been cut short: the matches returned, and whether more matched. */
export interface LabTestSearchPage {
  tests: QuestTestCode[];
  capped: boolean;
}

export interface LabOrderConfig {
  vendor: string;
  title: string;
  /** Heading on the success screen. Defaults to "Order Submitted"; Axiom uses
   *  "Order Created" since the order still needs payment before it's sent. */
  successTitle?: string;
  /** Require a complete shipping address (street/city/state/zip) before submit.
   *  Set for ship-to-patient integrations (SiPhox, Axiom). */
  requireAddress?: boolean;
  /** Restrict the ship-to state to this set (2-letter codes). Axiom only fills
   *  in the states its pharmacy is licensed in; ordering elsewhere is rejected. */
  allowedStates?: string[];
  /** Message shown when the ship-to state isn't in allowedStates. */
  allowedStatesMessage?: string;
  /**
   * Called with the ship-to state every time it changes — typed, picked from a
   * saved address, pasted, or loaded from the chart — so a vendor modal can
   * react to the destination before submit (Axiom locks items per state).
   */
  onShipStateChange?: (state: string) => void;
  /** Called when a vendor-supplied Ship to choice is picked (its key) or left (null). */
  onShipToChoiceChange?: (key: string | null) => void;
  /** Lock patient identity (name + DOB) to the record — shown read-only so the
   *  order can't silently diverge from the chart. Set for Axiom peptides. */
  lockPatientIdentity?: boolean;
  /**
   * Lock each identity field (name, DOB, gender) the chart already has, and
   * leave a blank one editable so the order can fill the gap. For vendors
   * whose server sends the chart's value for a filled field and the form's
   * only for a blank one (Access, Vibrant: LabOrderDemographics), so what
   * staff confirm is what is sent.
   */
  lockFilledIdentity?: boolean;
  /**
   * Lock Cell Phone and E-mail to the chart. For staff-placed kits (SiPhox)
   * where the vendor contacts the patient, so the order must not carry
   * contact details the chart lacks. A blank Cell Phone stays editable (it
   * travels on the order as the shipping phone). E-mail is always read-only:
   * the vendor reads it from the chart alone, so a blank one blocks Send
   * Order until it is added to the chart.
   */
  lockPatientContact?: boolean;
  /**
   * Under lockPatientIdentity, a chart gender that is not female/male
   * ("Other") unlocks the Gender field: staff pick Female or Male for this
   * order only, it travels on the order as `orderSex`, and the chart keeps its
   * answer (SCRUM-1873). For a vendor whose server reads the order's sex when
   * the chart has none (Axiom). Without it "Other" stays locked, because those
   * vendors read sex from the chart alone.
   */
  orderGenderForOther?: boolean;
  /**
   * Wording of the address block for a vendor whose address is not a delivery
   * (GetLabs: where the phlebotomist goes). Defaults to the shipping wording.
   */
  addressWording?: { label: string; notice: string; editHint?: string };
  /**
   * The patient's age (from the DOB) must fall in this range to submit
   * (SCRUM-2007). Charts are often saved with the current year as the birth
   * year, or with no DOB at all — common for app sign-ups — and the order then
   * goes to the pharmacy for a "0-year-old". When the DOB on file is missing or
   * out of range, the DOB fields unlock (even under lockPatientIdentity), Send
   * Order is blocked until it is corrected, and the corrected DOB is saved back
   * to the chart through `demographicsFill`.
   */
  patientAgeRange?: { min: number; max: number };
  features: {
    presets: boolean;
    /** Facility / Provider / Business Unit / Lab Collect top bar. */
    facilityProvider: boolean;
    /**
     * Provider dropdown ONLY (no Facility / Business Unit / Lab Collect).
     * For vendors (e.g. Vibrant) that require an ordering provider but have no
     * facility/business-unit concept. Ignored when facilityProvider is true.
     */
    providerPicker?: boolean;
    /**
     * Location dropdown (rendered to the LEFT of Provider). For vendors (e.g.
     * Vibrant) whose catalog + providers are scoped per location (routingId).
     * When set, the selected location filters providers and test search, and a
     * location must be chosen before submitting.
     */
    locationPicker?: boolean;
    /**
     * Run the test search on focus / with an empty query so the dropdown
     * populates immediately (Vibrant). Quest leaves this off (search-as-you-type).
     */
    testSearchOnEmpty?: boolean;
    /** Bill To (insurance) section. */
    billing: boolean;
    /** Diagnoses section (only shown when billing has a third-party entry). */
    diagnoses: boolean;
    /**
     * Show the Tests search section and require ≥1 test before submitting.
     * Set to false for integrations that use custom line items (SiPhox kit
     * types, Axiom product picks, etc.) and provide them via topSlot instead.
     * Defaults to true when searchTests is provided.
     */
    tests?: boolean;
    /**
     * All notes UI — the demographics "PID Notes" / Patient Internal/External
     * Comment block AND the order-level Internal Notes / External Notes sections.
     * Defaults to true; set false for ship-to-patient Rx integrations (Axiom)
     * that don't carry lab notes.
     */
    notes?: boolean;
    /**
     * Collection Date field. Defaults to true; set false for prescribed Rx
     * orders (Axiom) where there is no specimen collection.
     */
    collectionDate?: boolean;
    /**
     * A collection TIME next to the order's collection date (Vibrant). Every
     * test's collectionDate is then sent as 'YYYY-MM-DDTHH:MM', the clinic's
     * wall clock; without it the lab records the specimen at midnight. Off by
     * default, so Quest keeps sending the date alone.
     */
    collectionTime?: boolean;
    /**
     * Height / Weight / Blood Pressure vitals in Required Details. Defaults to
     * true; set false for integrations that don't need clinical vitals (Axiom).
     */
    vitals?: boolean;
  };
  /**
   * Temporarily hide the Preset / Prior Order toolbar without removing preset
   * support. Set for Vibrant while that section is disabled in the UI.
   */
  hidePresetsToolbar?: boolean;
  /**
   * Render the preset controls inside the Location bar, after Location, and
   * keep them disabled until a location is chosen. For vendors whose presets
   * are per location (Vibrant); requires features.locationPicker.
   */
  presetsInLocationBar?: boolean;
  /**
   * Render the Tests section above Required Details instead of below it.
   * Set for Vibrant per product request.
   */
  testsBeforeDetails?: boolean;
  /**
   * Optional search function for the Tests section. Required when features.tests !== false.
   * May answer with a LabTestSearchPage to say the matches were cut short.
   */
  searchTests?: (query: string, routingId?: string) => Promise<QuestTestCode[] | LabTestSearchPage>;
  /**
   * Words under the test search (Access, whose catalog is too large to list):
   * `prompt` while the box is empty, `noMatch` when a search found nothing,
   * `capped` above results that were cut short. Unset: matches only, as before.
   */
  testSearchMessages?: { prompt: string; noMatch: string; capped: string };
  /** The name shown for a test in the search, the selected list and the confirmation. Unset: its description. */
  describeTest?: (testCode: string, description: string) => string;
  /**
   * What to say when the vendor cannot be ordered from yet: the account has no
   * locations (`noLocation`), or the chosen location has no providers
   * (`noProvider`). Either one is shown and Send Order is disabled. Unset: the
   * form shows its own hint under Provider and leaves Send Order enabled.
   */
  setupMessages?: { noLocation: string; noProvider: string };
  /** Where an administrator edits presets, for the left-out-tests notice. Defaults to Vibrant's. */
  presetAdminPath?: string;
  getFacilities?: () => Promise<QuestFacility[]>;
  getProviders?: (routingId?: string) => Promise<QuestProvider[]>;
  /** Locations for the Location dropdown (Vibrant). Required when features.locationPicker. */
  getLocations?: () => Promise<Array<{ routingId: string; routingTitle: string }>>;
  searchInsurance?: (query: string) => Promise<QuestInsurance[]>;
  searchDiagnoses?: (query: string) => Promise<QuestDiagnosis[]>;
  getPresets?: (patientId: number, routingId?: string) => Promise<{ presets: QuestPresetItem[]; orders: QuestPresetItem[] }>;
  /** `unavailableTests`: stored tests the server left out because they can no longer be ordered. */
  getPresetContent?: (type: string, id: number) => Promise<{
    type: string;
    fullOrderContent: string;
    title: string;
    unavailableTests?: Array<{ testCode: string; description: string }>;
  }>;
  parsePreset?: (content: string, kind: 'order' | 'preset', defaultDate: string) => ParsedLabPreset;
  savePreset?: (payload: QuestOrderPayload & { presetTitle: string; presetShared: string }) => Promise<unknown>;
  /**
   * The vendor's own checks, run by "Send Order" after the form's. Return a
   * message to stop on the form and show it, or null to continue to the
   * confirmation step. For what the vendor needs that the form does not
   * know about (Partell: the prescriptions and the prescriber).
   */
  validateExtra?: (payload: QuestOrderPayload) => string | null;
  /** Build + transmit the order; returns a normalized result. */
  submit: (payload: QuestOrderPayload) => Promise<LabOrderSubmitResult>;
  /** Optional one-line auth/debug summary extractor for the error banner. */
  extractErrorDebug?: (err: unknown) => string | null;
}

interface SelectedTest {
  testCode: string;
  description: string;
  label: string;
  collectionDate: string;
  obrNotes: QuestOrderNote[];
  /**
   * Location picker vendors: the location this code belongs to ('' = every
   * location). Decides whether the test survives a location change; absent
   * means unknown, and the test is kept.
   */
  routingId?: string;
}

/**
 * The heads-up shown when a loaded preset / prior order holds tests that can
 * no longer be ordered (retired, or moved to another location since it was
 * saved); the server leaves them out. Null when there are none.
 */
function unavailableTestsNotice(
  tests: Array<{ testCode: string; description: string }>,
  kind: 'preset' | 'order',
  adminPath = 'Admin > Integrations > Vibrant > Presets',
  describeTest?: (testCode: string, description: string) => string,
): string | null {
  if (tests.length === 0) return null;
  const nameless = (code: string) => (describeTest ? describeTest(code, '') : code);
  const names = tests.map((t) => (t.description ? `${t.description} (${t.testCode})` : nameless(t.testCode))).join(', ');
  const count = tests.length === 1 ? '1 test' : `${tests.length} tests`;
  const verb = tests.length === 1 ? 'was' : 'were';
  const source = kind === 'order' ? 'this prior order' : 'this preset';
  const fix = kind === 'preset' ? ` An administrator can update the preset under ${adminPath}.` : '';
  return `${count} in ${source} can no longer be ordered and ${verb} left out: ${names}.${fix}`;
}

/** The selected tests that can stay when the location becomes `routingId`. */
function testsKeptAtLocation<T extends { routingId?: string }>(tests: T[], routingId: string): T[] {
  return tests.filter((t) => t.routingId === undefined || t.routingId === '' || t.routingId === routingId);
}

export interface InsuranceEntry {
  billingType: string;
  insuranceId: string;
  insuranceName: string;
  policyNumber: string;
  groupNumber: string;
  coStreet: string;
  coCity: string;
  coState: string;
  coZip: string;
  relationship: string;
  guarantorFirstName: string;
  guarantorMiddleName: string;
  guarantorLastName: string;
  guarantorAddress: string;
  guarantorCity: string;
  guarantorState: string;
  guarantorZip: string;
  guarantorPhone: string;
  insuredFirstName: string;
  insuredMiddleName: string;
  insuredLastName: string;
  insuredAddress: string;
  insuredCity: string;
  insuredState: string;
  insuredZip: string;
}

/**
 * A ship-to already chosen for an order, used to reopen it as it was saved.
 * Every field optional: an older order may predate any of them.
 */
/**
 * An extra entry in the Ship to menu, supplied by the vendor form — Partell's
 * "Clinic", which ships to the practice address. Picking it fills the address
 * from `address` (read-only while it is picked) and is reported through
 * config.onShipToChoiceChange and the payload's `shipToChoice`.
 */
export interface ShipToChoice {
  key: string;
  label: string;
  address: { recipientName: string; line1: string; line2?: string; city: string; state: string; zip: string };
  /**
   * Why this choice can't be used right now (e.g. the practice address is
   * incomplete). Shown by Send Order in place of "Street address is required",
   * which would point at a field that can't be edited while it is picked.
   */
  incompleteReason?: string | null;
}

export interface InitialShipTo {
  recipientName?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  phone?: string | null;
  patientShippingAddressId?: number | null;
}

interface LabOrderModalCoreProps {
  config: LabOrderConfig;
  patientId: number;
  open: boolean;
  onClose: () => void;
  onOrdered: () => void;
  /**
   * Seed the ship-to from an address already chosen, instead of defaulting to
   * the patient's chart (SCRUM-1814).
   *
   * Reopening an order for editing MUST show the address that order carries.
   * Without this the form would fill from the chart, so staff correcting a
   * delivery address would be shown a different one and silently overwrite
   * their earlier choice by saving — the chart is frequently blank or stale,
   * which is what stranded paid orders in the first place (#661).
   *
   * A missing field falls through to the chart, so a partial saved address
   * cannot produce an empty form.
   */
  initialShipTo?: InitialShipTo | null;
  /**
   * The sex an existing order was placed with (its metadata.patientSex), for
   * an edit or re-order under config.orderGenderForOther. On an "Other" chart
   * it pre-selects the Gender pick so staff see — and keep — the saved value
   * instead of choosing again blind. Ignored when the chart says female/male
   * or is blank (SCRUM-1873).
   */
  initialOrderSex?: 'F' | 'M' | null;
  /** Vendor-supplied Ship to choices, listed after the patient's addresses. */
  shipToChoices?: ShipToChoice[];
  /**
   * Vendor-specific content rendered above the Required Details section.
   * Use this for serviceability notices, price cards, coverage banners, etc.
   */
  topSlot?: React.ReactNode;
  /**
   * Vendor-specific content rendered in the same position as the Tests
   * section — after Required Details. Use this for kit type pickers (SiPhox),
   * item selectors (Axiom), etc. Only rendered when features.tests is false.
   */
  itemsSlot?: React.ReactNode;
  /**
   * Read-only summary of the custom items, shown on the confirmation screen
   * in place of the Tests list. If omitted the Tests block is simply hidden.
   */
  confirmSlot?: React.ReactNode;
}

/** The distinct collection dates the tests go out with, or the order's date when there are none. */
function sentCollectionDates(tests: Array<{ collectionDate: string }>, fallback: string): string {
  const dates = Array.from(new Set(tests.map((t) => t.collectionDate).filter(Boolean)));
  return dates.length > 0 ? dates.join(', ') : fallback;
}

function emptyInsurance(seed: Partial<InsuranceEntry> = {}): InsuranceEntry {
  return {
    billingType: 'C',
    insuranceId: '',
    insuranceName: '',
    policyNumber: '',
    groupNumber: '',
    coStreet: '',
    coCity: '',
    coState: '',
    coZip: '',
    relationship: '',
    guarantorFirstName: '',
    guarantorMiddleName: '',
    guarantorLastName: '',
    guarantorAddress: '',
    guarantorCity: '',
    guarantorState: '',
    guarantorZip: '',
    guarantorPhone: '',
    insuredFirstName: '',
    insuredMiddleName: '',
    insuredLastName: '',
    insuredAddress: '',
    insuredCity: '',
    insuredState: '',
    insuredZip: '',
    ...seed,
  };
}

export function LabOrderModalCore({ config, patientId, open, onClose, onOrdered, initialShipTo, initialOrderSex, shipToChoices, topSlot, itemsSlot, confirmSlot }: LabOrderModalCoreProps) {
  const { detail } = usePatientDetail(String(patientId));
  const patient = detail.patient as Record<string, string | null | undefined> | null;
  const userLevel = useAuthStore((s) => s.user?.userLevel ?? 0);

  const showFacility = config.features.facilityProvider;
  const showProvider = config.features.facilityProvider || !!config.features.providerPicker;
  const showLocation = !!config.features.locationPicker;
  const testSearchOnEmpty = !!config.features.testSearchOnEmpty;
  const showBilling = config.features.billing;
  const showPresets = config.features.presets;
  const showNotes = config.features.notes !== false;
  const showCollectionDate = config.features.collectionDate !== false;
  const showCollectionTime = showCollectionDate && !!config.features.collectionTime;
  const showVitals = config.features.vitals !== false;
  const showTests = config.features.tests !== false && !!config.searchTests;

  const [step, setStep] = useState<'form' | 'confirm' | 'result'>('form');
  const [error, setError] = useState<string | null>(null);
  // Set by a submit result with lockSend (saved, not sent); cleared on close.
  const [sendLocked, setSendLocked] = useState(false);
  /** Non-blocking heads-up, e.g. tests a loaded preset could no longer include. */
  const [notice, setNotice] = useState<string | null>(null);
  const [errorDebug, setErrorDebug] = useState<string | null>(null);
  const [resultLines, setResultLines] = useState<string[]>([]);

  // Top nav
  const [facilityId, setFacilityId] = useState('');
  const [providerId, setProviderId] = useState('');
  const [routingId, setRoutingId] = useState('');
  const [businessUnit, setBusinessUnit] = useState('Default');
  const [labCollect, setLabCollect] = useState(false);
  const [collectionDate, setCollectionDate] = useState(todayYmd);
  /** 'HH:MM' (24h) when features.collectionTime; '' while incomplete. */
  const [collectionTime, setCollectionTime] = useState(nowHhmm);
  /** The time that goes out with the order: '' for vendors that take none. */
  const sentCollectionTime = showCollectionTime ? collectionTime : '';

  // Required Details
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [weight, setWeight] = useState('');
  const [bpSystolic, setBpSystolic] = useState('');
  const [bpDiastolic, setBpDiastolic] = useState('');
  const [street, setStreet] = useState('');
  const [street2, setStreet2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('United States');
  // Ship-to (requireAddress vendors only): who receives the package + which
  // saved patient_shipping_addresses row was picked ('' = typing a new one).
  const [recipientName, setRecipientName] = useState('');
  const [shippingAddressId, setShippingAddressId] = useState<number | null>(null);
  /** The vendor Ship to choice picked, if any (see ShipToChoice). */
  const [shipChoiceKey, setShipChoiceKey] = useState<string | null>(null);
  const shipChoice = (shipToChoices ?? []).find((c) => c.key === shipChoiceKey) ?? null;
  const onShipToChoiceChangeRef = useRef(config.onShipToChoiceChange);
  onShipToChoiceChangeRef.current = config.onShipToChoiceChange;
  useEffect(() => { if (!open) setShipChoiceKey(null); }, [open]);
  useEffect(() => { onShipToChoiceChangeRef.current?.(shipChoiceKey); }, [shipChoiceKey]);
  // While a choice is picked its address IS the ship-to — kept in step if it
  // changes underneath (the practice address loads late, or is edited).
  useEffect(() => {
    if (!shipChoice) return;
    const a = shipChoice.address;
    setRecipientName(a.recipientName);
    setStreet(a.line1);
    setStreet2(a.line2 ?? '');
    setCity(a.city);
    setState(a.state);
    setZip(a.zip);
    // A choice ships within the US (the practice), so its country is fixed too.
    setCountry('United States');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipChoice?.key, shipChoice?.address.recipientName, shipChoice?.address.line1, shipChoice?.address.line2, shipChoice?.address.city, shipChoice?.address.state, shipChoice?.address.zip]);
  const onShipStateChangeRef = useRef(config.onShipStateChange);
  onShipStateChangeRef.current = config.onShipStateChange;
  useEffect(() => {
    if (open) onShipStateChangeRef.current?.(state);
  }, [open, state]);
  /** What the chart wrote into the ship-to on open — see the saved-address effect. */
  const chartSeedRef = useRef<{ line1?: string; city?: string; state?: string }>({});
  const savedSeedAppliedRef = useRef(false);
  const [cellphone, setCellphone] = useState('');
  const [email, setEmail] = useState('');
  const [gender, setGender] = useState('NS');
  // Which of the fillable demographics were blank on the patient record when the
  // modal opened — see the pre-fill effect and buildDemographicsFill().
  const missingOnOpenRef = useRef<Record<string, boolean>>({});
  /**
   * The DOB on file was missing or outside config.patientAgeRange when the
   * modal opened: the DOB fields unlock so it can be corrected here, and the
   * correction is written back to the chart. Captured on open, like
   * missingOnOpenRef, so typing a valid date does not re-lock the fields.
   */
  const [dobOnFileInvalid, setDobOnFileInvalid] = useState(false);
  // The chart's gender as it was on open (SCRUM-1873). Under
  // lockPatientIdentity a blank chart unlocks the field — the pick is saved to
  // the chart before the order. An answer that is not female/male ("Other")
  // stays locked unless config.orderGenderForOther: then the pick is sent with
  // this order only and the chart keeps its answer.
  const [genderOnFile, setGenderOnFile] = useState<'usable' | 'blank' | 'other'>('usable');
  // "Other" chart, Axiom-style vendor: the Gender pick is for this order only.
  const orderGenderOnly = !!config.lockPatientIdentity && !!config.orderGenderForOther && genderOnFile === 'other';

  // PID Notes
  const [pidNotes, setPidNotes] = useState<QuestOrderNote[]>([{ text: '', type: 'I' }]);
  const [pidInternalNote, setPidInternalNote] = useState('');
  const [pidExternalNote, setPidExternalNote] = useState('');

  // Tests
  const [selectedTests, setSelectedTests] = useState<SelectedTest[]>([]);
  const [testSearch, setTestSearch] = useState('');
  const [testSearchResults, setTestSearchResults] = useState<QuestTestCode[]>([]);
  const [testSearching, setTestSearching] = useState(false);
  // config.testSearchMessages only: whether the last results were cut short,
  // the query they answered, and whether the box has been used since the last
  // pick (so the prompt shows on focus, not on open).
  const [testSearchCapped, setTestSearchCapped] = useState(false);
  const [testSearchedQuery, setTestSearchedQuery] = useState<string | null>(null);
  const [testSearchPrompted, setTestSearchPrompted] = useState(false);
  const testSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetTestSearchState = useCallback(() => {
    setTestSearchCapped(false);
    setTestSearchedQuery(null);
    setTestSearchPrompted(false);
  }, []);

  // Bill To
  const [insuranceEntries, setInsuranceEntries] = useState<InsuranceEntry[]>([]);

  // Diagnoses
  const [diagnosisEntries, setDiagnosisEntries] = useState<QuestDiagnosis[]>([]);
  const [dxSearch, setDxSearch] = useState('');
  const [dxSearchResults, setDxSearchResults] = useState<QuestDiagnosis[]>([]);
  const dxSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Internal / External notes
  const [internalNotes, setInternalNotes] = useState<string[]>([]);
  const [externalNotes, setExternalNotes] = useState<string[]>([]);

  // Presets
  const [presetValue, setPresetValue] = useState('');
  const [presetLoading, setPresetLoading] = useState(false);
  const { data: presetData, refetch: refetchPresets } = useQuery({
    queryKey: [config.vendor, 'presets', patientId, routingId],
    queryFn: () => config.getPresets!(patientId, routingId || undefined),
    // With a location picker and no location yet, this lists every preset;
    // once one is picked, that location's plus the all-locations ones.
    enabled: open && showPresets && !!config.getPresets,
    staleTime: 60 * 1000,
  });

  // Section collapse state
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['details', 'tests', 'billTo']));

  const { data: facilities } = useQuery({
    queryKey: [config.vendor, 'facilities'],
    queryFn: () => config.getFacilities!(),
    enabled: open && showFacility && !!config.getFacilities,
    staleTime: 10 * 60 * 1000,
  });

  // Saved ship-to addresses for the picker (requireAddress vendors only).
  const { data: savedAddresses, refetch: refetchSavedAddresses } = useQuery({
    queryKey: ['patient-shipping-addresses', patientId],
    queryFn: () => getPatientShippingAddresses(patientId),
    enabled: open && !!config.requireAddress,
    staleTime: 60 * 1000,
  });

  /** Restore the ship-to fields to the patient's profile address (the default).
   *  Does NOT modify the patient record — just repopulates the order's ship-to. */
  function resetToPatientAddress() {
    setStreet(patient?.address ?? patient?.street ?? '');
    setStreet2((patient as { address2?: string } | null)?.address2 ?? '');
    setCity(patient?.city ?? '');
    setState(patient?.state ?? '');
    const z = patient?.zip ?? '';
    setZip(z === '0' ? '' : z);
    setRecipientName(
      `${patient?.firstName ?? patient?.firstname ?? ''} ${patient?.lastName ?? patient?.lastname ?? ''}`.trim(),
    );
  }

  /** Fill the address block from a saved entry. */
  function applySavedAddress(a: PatientShippingAddress) {
    setShippingAddressId(a.patientShippingAddressId);
    setRecipientName(a.recipientName);
    setStreet(a.line1);
    setStreet2(a.line2 ?? '');
    setCity(a.city);
    setState(a.state);
    setZip(a.zip);
    setCountry(a.country || 'United States');
    // A locked chart phone stays: the saved address's phone would otherwise
    // replace it on the order, and staff could not put it back.
    if (a.phone && !contactLocked('cellphone')) setCellphone(a.phone);
  }

  // True when the address currently in the form matches an already-saved entry
  // (so we hide the "Save address" button — nothing new to save).
  const currentAddressIsSaved = (savedAddresses ?? []).some(
    (a) =>
      a.recipientName.trim().toLowerCase() === recipientName.trim().toLowerCase() &&
      a.line1.trim().toLowerCase() === street.trim().toLowerCase() &&
      a.city.trim().toLowerCase() === city.trim().toLowerCase() &&
      a.state.trim().toLowerCase() === state.trim().toLowerCase() &&
      a.zip.trim() === zip.trim(),
  );

  const saveAddressMutation = useMutation({
    mutationFn: () =>
      savePatientShippingAddress(patientId, {
        recipientName: recipientName.trim(),
        phone: cellphone.trim() || undefined,
        line1: street.trim(),
        line2: street2.trim() || undefined,
        city: city.trim(),
        state: state.trim(),
        zip: zip.trim(),
        country: country.trim() || undefined,
      }),
    onSuccess: async (saved) => {
      setError(null);
      await refetchSavedAddresses();
      setShippingAddressId(saved.patientShippingAddressId);
    },
    onError: (err: unknown) => setError(getErrorMessage(err, 'Could not save this address.')),
  });

  /** Address is complete enough to save to the book. */
  const canSaveAddress =
    !!recipientName.trim() && !!street.trim() && !!city.trim() && !!state.trim() && !!zip.trim();

  const { data: locations } = useQuery({
    queryKey: [config.vendor, 'locations'],
    queryFn: () => config.getLocations!(),
    enabled: open && showLocation && !!config.getLocations,
    staleTime: 10 * 60 * 1000,
  });

  const { data: providers, isFetching: providersFetching } = useQuery({
    queryKey: [config.vendor, 'providers', routingId],
    queryFn: () => config.getProviders!(routingId || undefined),
    // When a location picker is in play, wait for a location before loading
    // providers (they are filtered per location).
    enabled: open && showProvider && !!config.getProviders && (!showLocation || !!routingId),
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (!showFacility) return;
    if (facilities?.length === 1 && !facilityId) {
      setFacilityId(facilities[0].sendingFacility);
    }
  }, [facilities, facilityId, showFacility]);

  useEffect(() => {
    if (!showProvider) return;
    if (providers?.length === 1 && !providerId) {
      setProviderId(providers[0].providerId);
    }
  }, [providers, providerId, showProvider]);

  // Some Vibrant routing ids are actually provider ids. When a location is
  // selected and the provider dropdown contains a provider whose id matches the
  // selected routing id exactly, auto-select it, overriding any other
  // association. Runs once per location / provider-list change (not on
  // providerId) so a manual change afterward still sticks.
  useEffect(() => {
    if (!showProvider || !routingId) return;
    const exact = providers?.find((p) => p.providerId === routingId);
    if (exact) {
      setProviderId(exact.providerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers, routingId, showProvider]);

  useEffect(() => {
    if (!showLocation) return;
    if (locations?.length === 1 && !routingId) {
      setRoutingId(locations[0].routingId);
    }
  }, [locations, routingId, showLocation]);

  // Pre-fill patient demographics on open
  useEffect(() => {
    if (!open || !patient) return;
    setFirstName(patient.firstName ?? patient.firstname ?? '');
    setMiddleName(patient.middleName ?? patient.middlename ?? '');
    setLastName(patient.lastName ?? patient.lastname ?? '');

    const dob = patient.birthDate ?? patient.birthdate ?? '';
    let dobParts: [string, string, string] = ['', '', ''];
    if (dob) {
      const parts = dob.split('-');
      if (parts.length === 3) {
        dobParts = [
          parts[0] === '0000' ? '' : parts[0],
          parts[1] === '00' ? '' : parts[1],
          (parts[2] === '00' ? '' : parts[2]).slice(0, 2),
        ];
        setBirthYear(dobParts[0]);
        setBirthMonth(dobParts[1]);
        setBirthDay(dobParts[2]);
      }
    }
    if (config.patientAgeRange) {
      const age = ageFromDob(...dobParts);
      setDobOnFileInvalid(age === null || age < config.patientAgeRange.min || age > config.patientAgeRange.max);
    }

    setCellphone(patient.cellPhone ?? patient.homePhone ?? '');
    setEmail(patient.email ?? '');
    setStreet(patient.address ?? patient.street ?? '');
    setStreet2((patient as { address2?: string }).address2 ?? '');
    setCity(patient.city ?? '');
    setState(patient.state ?? '');
    const z = patient.zip ?? '';
    setZip(z === '0' ? '' : z);

    // "Female"/"Male" are on thousands of charts, not just "F"/"M".
    const genderCode = toGenderCode(patient.gender);
    const onFile = genderCode !== null ? 'usable' : isBlankGender(patient.gender) ? 'blank' : 'other';
    // An "Other" chart re-opening an order (edit / re-order) restores the sex
    // that order was placed with — the chart itself is never changed.
    const restoredOrderSex = onFile === 'other' && config.lockPatientIdentity && config.orderGenderForOther
      && (initialOrderSex === 'F' || initialOrderSex === 'M') ? initialOrderSex : null;
    setGender(genderCode ?? restoredOrderSex ?? 'NS');
    setGenderOnFile(onFile);

    // Record which demographics the patient record was MISSING when this modal
    // opened. A lab order may fill a gap, never change an answer — the patient
    // profile is the only place demographics are edited. Captured here rather
    // than read at submit time so later form edits cannot widen the set.
    const zipStored = (patient.zip ?? '') === '0' ? '' : (patient.zip ?? '');
    missingOnOpenRef.current = {
      firstName: (patient.firstName ?? patient.firstname ?? '').trim() === '',
      middleName: (patient.middleName ?? patient.middlename ?? '').trim() === '',
      lastName: (patient.lastName ?? patient.lastname ?? '').trim() === '',
      birthDate: !dob || dob.startsWith('0000') || dob === '',
      address: (patient.address ?? patient.street ?? '').trim() === '',
      address2: ((patient as { address2?: string }).address2 ?? '').trim() === '',
      city: (patient.city ?? '').trim() === '',
      state: (patient.state ?? '').trim() === '',
      zip: zipStored.trim() === '',
      // Not female/male on the chart is a gap: a vendor that writes
      // demographicsFill back (Genova) stores the F/M it sends, so the chart
      // and the order agree. Locked vendors never get a pick for "Other".
      gender: genderCode === null,
      cellphone: (patient.cellPhone ?? patient.homePhone ?? '').trim() === '',
      email: (patient.email ?? '').trim() === '',
    };

    // Ship-to defaults to the patient themselves at their profile address —
    // unless the caller supplied one already chosen for this order, in which
    // case that is what the order actually ships to and what staff must see
    // (SCRUM-1814). Applied per field: an older order missing a line falls back
    // to the chart rather than opening the form with a hole in it.
    const chartName =
      `${patient.firstName ?? patient.firstname ?? ''} ${patient.lastName ?? patient.lastname ?? ''}`.trim();
    const seeded = (v: string | null | undefined): string => (v ?? '').trim();

    if (initialShipTo) {
      setRecipientName(seeded(initialShipTo.recipientName) || chartName);
      if (seeded(initialShipTo.line1)) setStreet(seeded(initialShipTo.line1));
      // Line 2 is copied even when empty: a saved address with no unit is a
      // statement, and inheriting the chart's apartment number would append a
      // stranger's unit to a valid street.
      if (seeded(initialShipTo.line1)) setStreet2(seeded(initialShipTo.line2));
      if (seeded(initialShipTo.city)) setCity(seeded(initialShipTo.city));
      if (seeded(initialShipTo.state)) setState(seeded(initialShipTo.state));
      if (seeded(initialShipTo.zip)) setZip(seeded(initialShipTo.zip));
      if (seeded(initialShipTo.phone)) setCellphone(seeded(initialShipTo.phone));
      setShippingAddressId(initialShipTo.patientShippingAddressId ?? null);
    } else {
      setRecipientName(chartName);
      setShippingAddressId(null);
      // Remember exactly what the chart put in the fields. The saved-address
      // seed below only fires while they still hold this, so a staff member who
      // starts typing before the addresses finish loading never has their work
      // replaced underneath them.
      chartSeedRef.current = {
        line1: (patient.address ?? patient.street ?? '') as string,
        city: (patient.city ?? '') as string,
        state: (patient.state ?? '') as string,
      };
      savedSeedAppliedRef.current = false;
    }

    setHeightFeet('');
    setHeightInches('');
    setWeight('');
    setBpSystolic('');
    setBpDiastolic('');
  }, [open, patient, initialShipTo, initialOrderSex]);

  /**
   * Where this patient's packages ACTUALLY go, when we know it.
   *
   * The chart address is where the patient lives on paper; the saved shipping
   * addresses are where deliveries have actually been sent, and the API returns
   * them most-recently-used first. Preferring the top one is what stops the
   * form quietly proposing an address the patient cannot receive at — prod
   * order 128 shipped a peptide to a stale chart address in another state and
   * was delivered there, while the address the clinic had chosen, and used
   * before, sat one row down in this very list.
   *
   * Deliberately NOT applied when the caller supplied `initialShipTo`: editing
   * or cloning an order must show that order's address, not the newest one.
   *
   * Only fires while the fields still hold what the chart put there. The
   * addresses arrive asynchronously, so without that guard a staff member who
   * started typing first would have it overwritten mid-keystroke.
   */
  useEffect(() => {
    if (!open || initialShipTo) return;
    if (savedSeedAppliedRef.current) return;
    const top = (savedAddresses ?? [])[0];
    if (!top) return;

    const seed = chartSeedRef.current;
    const untouched =
      street.trim() === (seed.line1 ?? '').trim() &&
      city.trim() === (seed.city ?? '').trim() &&
      state.trim() === (seed.state ?? '').trim();
    if (!untouched) return;

    savedSeedAppliedRef.current = true;
    setStreet(top.line1 ?? '');
    setStreet2(top.line2 ?? '');
    setCity(top.city ?? '');
    setState(top.state ?? '');
    setZip(top.zip ?? '');
    if ((top.recipientName ?? '').trim()) setRecipientName(top.recipientName as string);
    setShippingAddressId(top.patientShippingAddressId ?? null);
  }, [open, initialShipTo, savedAddresses, street, city, state]);

  // Quest only: auto-create one default Client billing entry on open.
  useEffect(() => {
    if (!open || !showBilling) return;
    setInsuranceEntries((prev) => (prev.length === 0 ? [emptyInsurance({ billingType: 'C' })] : prev));
  }, [open, showBilling]);

  // Reset form on close
  useEffect(() => {
    if (open) return;
    setStep('form');
    setSendLocked(false);
    setError(null);
    setNotice(null);
    setErrorDebug(null);
    setResultLines([]);
    setSelectedTests([]);
    setTestSearch('');
    setTestSearchResults([]);
    resetTestSearchState();
    setInsuranceEntries([]);
    setDiagnosisEntries([]);
    setPidNotes([{ text: '', type: 'I' }]);
    setPidInternalNote('');
    setPidExternalNote('');
    setInternalNotes([]);
    setExternalNotes([]);
    setFacilityId('');
    setProviderId('');
    setRoutingId('');
    setBusinessUnit('Default');
    setLabCollect(false);
    setPresetValue('');
    setCollectionDate(todayYmd());
    setCollectionTime(nowHhmm());
    setRecipientName('');
    setShippingAddressId(null);
  }, [open, resetTestSearchState]);

  /*
    Scroll lock, via the shared refcounted owner rather than this component writing
    `body.style.overflow` itself (SCRUM-1423).

    Two reasons the old two lines were wrong. Writing `''` unconditionally on cleanup
    unlocks the page for any OTHER overlay still holding it — this modal is opened from
    the Labs tab, which can have the AI overlay open behind it. And `overflow: hidden`
    on the body does not hold on iOS, nor does it restore the reader's position; the
    shared hook pins the body at its offset and puts it back.
  */
  useBodyScrollLock(open);

  // Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && step !== 'confirm') onClose(); };
    document.addEventListener('keydown', handler);
    return () => { document.removeEventListener('keydown', handler); };
  }, [open, onClose, step]);

  const runTestSearch = useCallback(async (query: string) => {
    if (!config.searchTests) return;
    // With a location picker, no location still searches: the adapter then
    // returns only the codes orderable at every location.
    setTestSearching(true);
    try {
      const found = await config.searchTests(query.trim(), routingId || undefined);
      const page = Array.isArray(found) ? { tests: found, capped: false } : found;
      setTestSearchResults(page.tests);
      setTestSearchCapped(page.capped);
      setTestSearchedQuery(query.trim());
    } catch {
      setTestSearchResults([]);
      setTestSearchCapped(false);
      setTestSearchedQuery(null);
    }
    setTestSearching(false);
  }, [config, routingId]);

  const handleTestSearch = useCallback((query: string) => {
    if (!config.searchTests) return;
    setTestSearch(query);
    if (config.testSearchMessages) setTestSearchPrompted(true);
    if (testSearchTimer.current) clearTimeout(testSearchTimer.current);
    if (query.trim().length < 1 && !testSearchOnEmpty) { setTestSearchResults([]); setTestSearchCapped(false); return; }
    setTestSearching(true);
    testSearchTimer.current = setTimeout(() => { void runTestSearch(query); }, 300);
  }, [config, testSearchOnEmpty, runTestSearch]);

  // Show the list immediately on focus (Vibrant); no-op for search-as-you-type vendors.
  const handleTestFocus = useCallback(() => {
    if (config.testSearchMessages) setTestSearchPrompted(true);
    if (!testSearchOnEmpty) return;
    if (testSearchTimer.current) clearTimeout(testSearchTimer.current);
    void runTestSearch(testSearch);
  }, [config.testSearchMessages, testSearchOnEmpty, runTestSearch, testSearch]);

  // When the selected location changes, the provider + catalog results are no
  // longer valid for it: reset them so the user re-picks within the new
  // location. Selected tests tied to another location go; tests orderable
  // everywhere stay.
  const handleLocationChange = useCallback((value: string) => {
    setRoutingId(value);
    setProviderId('');
    setSelectedTests((prev) => testsKeptAtLocation(prev, value));
    setTestSearch('');
    setTestSearchResults([]);
    resetTestSearchState();
    setPresetValue('');
  }, [resetTestSearchState]);

  const handleDxSearch = useCallback((query: string) => {
    setDxSearch(query);
    if (dxSearchTimer.current) clearTimeout(dxSearchTimer.current);
    if (query.trim().length < 1) { setDxSearchResults([]); return; }
    dxSearchTimer.current = setTimeout(async () => {
      try {
        const results = config.searchDiagnoses ? await config.searchDiagnoses(query.trim()) : [];
        setDxSearchResults(results);
      } catch { setDxSearchResults([]); }
    }, 300);
  }, [config]);

  function addTest(test: QuestTestCode) {
    if (selectedTests.some((t) => t.testCode === test.testCode)) return;
    setSelectedTests((prev) => [...prev, {
      testCode: test.testCode,
      description: test.description,
      label: test.label,
      collectionDate: collectionDate,
      obrNotes: [],
      ...(showLocation ? { routingId: test.routingId ?? '' } : {}),
    }]);
    setTestSearch('');
    setTestSearchResults([]);
    setTestSearchCapped(false);
    setTestSearchPrompted(false);
  }

  function removeTest(index: number) {
    setSelectedTests((prev) => prev.filter((_, i) => i !== index));
  }

  function updateTest(index: number, updates: Partial<SelectedTest>) {
    setSelectedTests((prev) => prev.map((t, i) => i === index ? { ...t, ...updates } : t));
  }

  function setAllCollectionDates(value: string) {
    setSelectedTests((prev) => prev.map((t) => ({ ...t, collectionDate: value })));
  }

  function addInsuranceEntry() {
    setInsuranceEntries((prev) => [...prev, emptyInsurance({
      guarantorFirstName: firstName,
      guarantorLastName: lastName,
      guarantorMiddleName: middleName,
      guarantorAddress: street,
      guarantorCity: city,
      guarantorState: state,
      guarantorZip: zip,
      guarantorPhone: cellphone,
      insuredFirstName: firstName,
      insuredLastName: lastName,
      insuredMiddleName: middleName,
      insuredAddress: street,
      insuredCity: city,
      insuredState: state,
      insuredZip: zip,
    })]);
  }

  function removeInsurance(index: number) {
    setInsuranceEntries((prev) => prev.filter((_, i) => i !== index));
  }

  function updateInsurance(index: number, updates: Partial<InsuranceEntry>) {
    setInsuranceEntries((prev) => prev.map((e, i) => i === index ? { ...e, ...updates } : e));
  }

  function addDiagnosis(dx: QuestDiagnosis) {
    if (diagnosisEntries.some((d) => d.code === dx.code)) return;
    setDiagnosisEntries((prev) => [...prev, dx]);
    setDxSearch('');
    setDxSearchResults([]);
  }

  function removeDiagnosis(index: number) {
    setDiagnosisEntries((prev) => prev.filter((_, i) => i !== index));
  }

  function toggleSection(key: string) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  const hasThirdParty = insuranceEntries.some((e) => e.billingType === 'T');
  const showDiagnoses = showBilling && config.features.diagnoses && hasThirdParty;

  async function handleLoadPreset() {
    if (!presetValue || !config.getPresetContent || !config.parsePreset) return;
    const [type, idStr] = presetValue.split('|');
    const id = Number(idStr);
    if (!type || !id) return;
    setPresetLoading(true);
    setError(null);
    setNotice(null);
    try {
      const content = await config.getPresetContent(type, id);
      setNotice(unavailableTestsNotice(content.unavailableTests ?? [], type === 'order' ? 'order' : 'preset', config.presetAdminPath, config.describeTest));
      const parsed = config.parsePreset(content.fullOrderContent, type === 'order' ? 'order' : 'preset', collectionDate);

      // A preset / prior order saved for a location brings that location with
      // it. Switching first drops tests tied to the old location, the same as
      // picking it by hand; one for all locations leaves the choice alone.
      const nextRoutingId = showLocation && parsed.routingId ? parsed.routingId : null;
      if (nextRoutingId !== null && nextRoutingId !== routingId) {
        handleLocationChange(nextRoutingId);
      }
      const locationFields = (t: { routingId?: string }) =>
        showLocation && t.routingId !== undefined ? { routingId: t.routingId } : {};

      if (type === 'order') {
        if (parsed.facility) setFacilityId(parsed.facility);
        if (parsed.provider) setProviderId(parsed.provider);
        if (parsed.patient) {
          setFirstName(parsed.patient.firstName);
          setLastName(parsed.patient.lastName);
          setBirthYear(parsed.patient.birthYear);
          setBirthMonth(parsed.patient.birthMonth);
          setBirthDay(parsed.patient.birthDay);
          setGender(toGenderCode(parsed.patient.gender) ?? 'NS');
          setStreet(parsed.patient.street);
          setCity(parsed.patient.city);
          setState(parsed.patient.state);
          setZip(parsed.patient.zip);
          setCellphone(parsed.patient.phone);
        }
        setPidNotes(parsed.pidNotes.length > 0 ? parsed.pidNotes : [{ text: '', type: 'I' }]);
        setInsuranceEntries(parsed.insurance.map((ins) => emptyInsurance(ins)));
        setDiagnosisEntries(parsed.diagnoses.map((d) => ({ code: d.code, title: d.title, label: `${d.code} - ${d.title}` })));
        setSelectedTests(parsed.tests.map((t) => ({
          testCode: t.testCode,
          description: t.description,
          label: `${t.testCode} - ${t.description}`,
          collectionDate: t.collectionDate,
          obrNotes: t.obrNotes,
          ...locationFields(t),
        })));
      } else {
        setSelectedTests((prev) => {
          const existing = new Set(prev.map((t) => t.testCode));
          const additions = parsed.tests
            .filter((t) => !existing.has(t.testCode))
            .map((t) => ({
              testCode: t.testCode,
              description: t.description,
              label: `${t.testCode} - ${t.description}`,
              collectionDate: t.collectionDate || collectionDate,
              obrNotes: t.obrNotes,
              ...locationFields(t),
            }));
          return [...prev, ...additions];
        });
      }
      // The location switch cleared the dropdown; show what was loaded.
      if (nextRoutingId !== null && nextRoutingId !== routingId) setPresetValue(presetValue);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load preset.'));
    } finally {
      setPresetLoading(false);
    }
  }

  /**
   * The ONLY demographics a lab order is allowed to write back, and only to fill
   * a gap: date of birth, the address parts, and sex/gender. Name, e-mail and
   * phone are never written from here — e-mail and phone are login identity and
   * carry verification state, and every demographic is edited in one place, the
   * patient profile.
   *
   * A field is included only when it was blank on the patient record at open AND
   * the form now holds a value, so an existing answer can never be overwritten.
   * Returns null when there is nothing to fill.
   */
  function buildDemographicsFill(): Record<string, string> | null {
    const missing = missingOnOpenRef.current;
    const fill: Record<string, string> = {};

    // A DOB that was out of range on file is a correction, not a change of
    // answer — it is the one demographic this form may overwrite.
    if ((missing.birthDate || dobOnFileInvalid) && ageFromDob(birthYear, birthMonth, birthDay) !== null) {
      fill.birthDate = `${birthYear}-${birthMonth.padStart(2, '0')}-${birthDay.padStart(2, '0')}`;
    }
    if (missing.address && street.trim())   fill.address  = street.trim();
    if (missing.address2 && street2.trim()) fill.address2 = street2.trim();
    if (missing.city && city.trim())        fill.city     = city.trim();
    if (missing.state && state)             fill.state    = state;
    if (missing.zip && zip.trim())          fill.zip      = zip.trim();
    // An "Other" chart under orderGenderForOther keeps its answer: the pick
    // travels on the order (orderSex), never to the chart.
    if (missing.gender && gender && gender !== 'NS' && !orderGenderOnly) fill.gender = gender;

    return Object.keys(fill).length > 0 ? fill : null;
  }

  function buildPayload(): QuestOrderPayload {
    return {
      patientId,
      facility: facilityId,
      provider: providerId,
      routingId,
      businessUnit,
      pscHold: labCollect,
      firstName,
      middleName,
      lastName,
      birthMonth,
      birthDay,
      birthYear,
      street,
      street2,
      city,
      state,
      zip,
      country,
      phone: cellphone,
      email,
      gender,
      ...(orderGenderOnly && (gender === 'F' || gender === 'M') ? { orderSex: gender } : {}),
      demographicsFill: buildDemographicsFill(),
      tests: selectedTests.map((t) => ({
        testCode: t.testCode,
        description: t.description,
        collectionDate: t.collectionDate && sentCollectionTime
          ? `${t.collectionDate}T${sentCollectionTime}`
          : t.collectionDate,
        obrNotes: t.obrNotes.filter((n) => n.text.trim() !== ''),
      })),
      pidNotes: pidNotes.filter((n) => n.text.trim() !== ''),
      pidInternalNote: pidInternalNote,
      pidExternalNote: pidExternalNote,
      insurance: insuranceEntries.map((e) => ({ ...e })),
      diagnoses: diagnosisEntries.map((d) => d.code),
      internalNotes: internalNotes.filter((n) => n.trim() !== ''),
      externalNotes: externalNotes.filter((n) => n.trim() !== ''),
      recipientName: recipientName.trim(),
      shippingAddressId,
      shipToChoice: shipChoiceKey,
    };
  }

  const savePresetMutation = useMutation({
    mutationFn: (args: { title: string; shared: string }) =>
      config.savePreset!({ ...buildPayload(), presetTitle: args.title, presetShared: args.shared }),
    onSuccess: () => {
      setError(null);
      setErrorDebug(null);
      void refetchPresets();
    },
    onError: (err: unknown) => {
      setError(getErrorMessage(err, 'Failed to save preset.'));
      setErrorDebug(config.extractErrorDebug?.(err) ?? null);
    },
  });

  function handleSavePreset() {
    const title = window.prompt('Enter a title for this preset:');
    if (title === null) return;
    if (title.trim() === '') { setError('Please enter a title for this preset.'); return; }
    const shared = window.confirm('Share this preset with other users in your account?\n\nOK = Shared, Cancel = Not shared') ? '1' : '0';
    savePresetMutation.mutate({ title: title.trim(), shared });
  }

  function validate(): string | null {
    if (showLocation) {
      if (!routingId) return 'Please select a location.';
    }
    if (showFacility) {
      if (!facilityId || facilityId === 'NS') return 'Please select a facility.';
    }
    if (showProvider) {
      if (!providerId || providerId === 'NS') return 'Please select a provider.';
    }
    if (!firstName.trim()) return 'First name is required.';
    if (!lastName.trim()) return 'Last name is required.';
    if (!birthMonth || !birthDay || !birthYear) return 'Date of birth is required.';
    if (config.patientAgeRange) {
      const age = ageFromDob(birthYear, birthMonth, birthDay);
      if (age === null) return 'Enter a valid date of birth (MM/DD/YYYY).';
      if (age < config.patientAgeRange.min || age > config.patientAgeRange.max) {
        return `The date of birth makes the patient ${age} years old. Correct the date of birth to continue — the order can't be sent with it.`;
      }
    }
    if (config.lockPatientIdentity && genderOnFile === 'other' && !config.orderGenderForOther) {
      return 'Set Female or Male on the patient’s chart before sending this order.';
    }
    if (gender === 'NS') return 'Gender is required.';
    if (config.lockPatientContact && !email.trim()) return 'Add an e-mail to the patient’s chart before sending this order.';
    if (showCollectionTime && !collectionTime) return 'Enter the collection time.';
    if (showTests && selectedTests.length === 0) return 'At least one test is required.';
    // Ship-to-patient integrations (SiPhox, Axiom) can't fulfill without a full
    // shipping address. street2 (apt/suite) stays optional; country defaults.
    if (config.requireAddress) {
      if (shipChoice?.incompleteReason) return shipChoice.incompleteReason;
      if (!recipientName.trim()) return 'Recipient name is required.';
      if (!street.trim()) return 'Street address is required.';
      if (!city.trim()) return 'City is required.';
      if (!state.trim()) return 'State is required.';
      if (!zip.trim()) return 'ZIP code is required.';
    }
    // Ship-to-state licensing gate (Axiom — its pharmacy is only licensed in 34 states).
    if (config.allowedStates && state.trim() && !config.allowedStates.includes(state.trim().toUpperCase())) {
      return config.allowedStatesMessage
        ?? `This vendor can't ship to ${state.trim().toUpperCase()} yet. Choose a licensed state.`;
    }
    return config.validateExtra?.(buildPayload()) ?? null;
  }

  const orderMutation = useMutation({
    mutationFn: () => config.submit(buildPayload()),
    onSuccess: (result) => {
      if (!result.ok) {
        setError(result.errorMessage || 'The order could not be submitted.');
        setErrorDebug(null);
        if (result.lockSend) setSendLocked(true);
        setStep('form');
        return;
      }
      setResultLines(result.lines);
      setStep('result');
      onOrdered();
    },
    onError: (err: unknown) => {
      setError(getErrorMessage(err, 'Failed to submit lab order.'));
      setErrorDebug(config.extractErrorDebug?.(err) ?? null);
      setStep('form');
    },
  });

  if (!open) return null;

  const inputClass = 'rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
  const selectClass = inputClass + ' bg-white';
  const labelClass = 'block text-xs font-medium text-slate-600 mb-0.5';
  // Required-field helpers: a red border when the value is missing (matching the
  // street-address treatment) and a red asterisk on the label.
  const reqInput = (missing: boolean, base = inputClass) =>
    base + (missing ? ' border-red-400 focus:border-red-500 focus:ring-red-500' : '');
  const reqMark = <span className="text-red-500"> *</span>;
  // Patient identity (name + DOB) is read-only for integrations that lock it to
  // the chart (e.g. Axiom peptides), so an order can't diverge from the record.
  const lockIdentity = !!config.lockPatientIdentity;
  const lockedCls = ' bg-slate-100 text-slate-600 cursor-not-allowed';
  // lockFilledIdentity: a field the chart had on open is read-only; a blank
  // one (or one not known yet) stays editable.
  const filledOnOpen = (field: string) => !!config.lockFilledIdentity && missingOnOpenRef.current[field] === false;
  const fieldLocked = (field: string) => lockIdentity || filledOnOpen(field);
  const fieldLockCls = (field: string) => (fieldLocked(field) ? lockedCls : '');
  // DOB stays editable when the one on file was missing/out of range (SCRUM-2007).
  // A chart with no DOB at all also unlocks it, like a blank gender: the order
  // fills the gap and buildDemographicsFill() writes it back.
  const dobLocked = (lockIdentity && !dobOnFileInvalid && missingOnOpenRef.current.birthDate !== true) || filledOnOpen('birthDate');
  // lockPatientContact: a phone the chart had on open is read-only (a blank
  // one can be filled); e-mail is always read-only, it is never sent from here.
  const contactLocked = (field: 'cellphone' | 'email') =>
    !!config.lockPatientContact && (field === 'email' || missingOnOpenRef.current[field] === false);
  const dobLockCls = dobLocked ? lockedCls : '';
  // Gender: under lockPatientIdentity a blank chart unlocks it, and so does an
  // "Other" chart when the vendor takes a per-order sex (orderGenderForOther →
  // orderGenderOnly; SCRUM-1873) — otherwise "Other" stays locked. Under
  // lockFilledIdentity a chart F/M locks it, and blank or "Other" is a gap the
  // order fills.
  const genderLocked = (lockIdentity && genderOnFile !== 'blank' && !orderGenderOnly) || filledOnOpen('gender');
  const dobAge = ageFromDob(birthYear, birthMonth, birthDay);
  const dobBad = !!config.patientAgeRange && (dobAge === null || dobAge < config.patientAgeRange.min || dobAge > config.patientAgeRange.max);
  // Which Required-Details fields are missing right now.
  const genderMissing = gender === 'NS' || gender === '';
  const addrReq = !!config.requireAddress;

  const presets = presetData?.presets ?? [];
  const priorOrders = presetData?.orders ?? [];

  const testLabel = (testCode: string, description: string) =>
    config.describeTest ? config.describeTest(testCode, description) : description;

  // The words under the test search (config.testSearchMessages only).
  const searchMessages = config.testSearchMessages;
  const typedTestQuery = testSearch.trim();
  let testSearchMessage: string | null = null;
  if (searchMessages && testSearchResults.length === 0) {
    if (typedTestQuery === '') testSearchMessage = testSearchPrompted ? searchMessages.prompt : null;
    else if (!testSearching && testSearchedQuery === typedTestQuery) testSearchMessage = searchMessages.noMatch;
  }

  // Nothing to order from yet (config.setupMessages only): Send Order is disabled.
  const setupMessages = config.setupMessages;
  let setupProblem: string | null = null;
  if (setupMessages && showLocation && locations !== undefined && locations.length === 0) {
    setupProblem = setupMessages.noLocation;
  } else if (
    setupMessages && showLocation && showProvider && !!routingId &&
    providers !== undefined && !providersFetching && providers.length === 0
  ) {
    setupProblem = setupMessages.noProvider;
  }
  const sendBlocked = setupProblem !== null || sendLocked;

  /**
   * Preset / Prior Order select + Load + Save Preset. Rendered either as its
   * own toolbar (Quest) or inside the Location bar (presetsInLocationBar).
   * With no location picked every preset is offered, and loading one picks
   * its location.
   */
  const renderPresetControls = () => (
    <div className="flex flex-wrap items-end gap-2" data-testid="lab-preset-controls">
      <div>
        <label className={labelClass}>Preset / Prior Order</label>
        <select
          className={selectClass}
          style={{ minWidth: 220 }}
          value={presetValue}
          onChange={(e) => setPresetValue(e.target.value)}
          aria-label="Preset or prior order"
        >
          <option value="">Select preset...</option>
          {presets.length > 0 && (
            <optgroup label="Presets">
              {presets.map((p: QuestPresetItem) => (
                <option key={p.value} value={p.value}>{p.title}{p.shared ? ' (shared)' : ''}</option>
              ))}
            </optgroup>
          )}
          {priorOrders.length > 0 && (
            <optgroup label="Prior Orders">
              {priorOrders.map((o: QuestPresetItem) => (
                <option key={o.value} value={o.value}>{o.title}</option>
              ))}
            </optgroup>
          )}
        </select>
      </div>
      <Button size="sm" variant="secondary" disabled={!presetValue || presetLoading} onClick={handleLoadPreset}>
        {presetLoading ? <Spinner className="h-3.5 w-3.5" /> : 'Load'}
      </Button>
      {userLevel > 2 && config.savePreset && (
        <Button size="sm" variant="secondary" disabled={savePresetMutation.isPending} onClick={handleSavePreset}>
          <Save className="h-3.5 w-3.5 mr-1" />
          Save Preset
        </Button>
      )}
    </div>
  );

  // Portaled to <body> so it escapes the detail-page layout's stacking context —
  // otherwise the right patient panel (DetailRightPanel, z-50) can paint over it.
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-4 pb-4 max-md:p-0">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={step !== 'confirm' ? onClose : undefined} />
      <div className="relative z-10 w-full max-w-5xl max-h-[95vh] flex flex-col rounded-lg bg-white shadow-xl overflow-hidden max-md:h-full max-md:max-h-none max-md:rounded-none">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-3 flex-shrink-0 bg-slate-50 max-md:px-4">
          <h2 className="text-base font-semibold text-slate-900">{config.title}</h2>
          <div className="flex items-center gap-2">
            {step === 'form' && (
              <Button
                size="sm"
                disabled={sendBlocked}
                onClick={() => {
                  if (sendBlocked) return;
                  const msg = validate();
                  if (msg) { setError(msg); setErrorDebug(null); return; }
                  setError(null);
                  setErrorDebug(null);
                  setStep('confirm');
                }}
              >
                <CheckCircle className="h-3.5 w-3.5 mr-1" />
                Send Order
              </Button>
            )}
            <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Error */}
        {notice && (
          <div role="status" data-testid="lab-order-notice" className="mx-6 mt-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
            {notice}
          </div>
        )}
        {setupProblem && step === 'form' && (
          <div role="alert" data-testid="lab-order-setup" className="mx-6 mt-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
            {setupProblem}
          </div>
        )}
        {error && (
          <div className="mx-6 mt-3 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 whitespace-pre-line">
            {error}
            {errorDebug && (
              <div className="mt-1 text-[11px] font-mono text-red-600/80 break-all">
                {errorDebug}
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 max-md:px-4">
          {step === 'result' ? (
            <div className="py-16 text-center">
              <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
              <h3 className="text-3xl font-bold text-slate-800">{config.successTitle ?? 'Order Submitted'}</h3>
              <div className="mx-auto mt-4 max-w-xl space-y-2 text-xl leading-relaxed text-slate-700">
                {resultLines.length > 0
                  ? resultLines.map((line, i) => <p key={i}>{line}</p>)
                  : <p>The lab order has been submitted.</p>}
              </div>
              <Button size="lg" className="mt-8 text-lg px-8 py-3" onClick={onClose}>Close</Button>
            </div>
          ) : step === 'confirm' ? (
            <ConfirmationView
              showFacility={showFacility}
              showProvider={showProvider}
              showBilling={showBilling}
              showTests={showTests}
              confirmSlot={confirmSlot}
              facilityName={facilities?.find((f) => f.sendingFacility === facilityId)?.facilityName ?? facilityId}
              providerName={providers?.find((p) => p.providerId === providerId)?.providerName ?? providerId}
              businessUnit={businessUnit}
              collectionDate={collectionDate}
              collectionTime={sentCollectionTime}
              labCollect={labCollect}
              firstName={firstName}
              lastName={lastName}
              birthMonth={birthMonth}
              birthDay={birthDay}
              birthYear={birthYear}
              gender={gender}
              tests={config.describeTest ? selectedTests.map((t) => ({ ...t, description: testLabel(t.testCode, t.description) })) : selectedTests}
              insuranceEntries={insuranceEntries}
              diagnoses={diagnosisEntries}
              onBack={() => setStep('form')}
              onSubmit={() => orderMutation.mutate()}
              submitting={orderMutation.isPending}
            />
          ) : (
            <>
              {/* Preset toolbar */}
              {showPresets && !config.hidePresetsToolbar && !config.presetsInLocationBar && (
                <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="lab-preset-toolbar">
                  {renderPresetControls()}
                </div>
              )}

              {/* Vendor-specific top slot (kit picker, item selector, provider contact, etc.) */}
              {topSlot}

              {/*
                Top Nav Bar.

                Rendered only when it has something in it. Every child below is gated on
                a `config.features` flag, and the Axiom (peptide) config turns all of
                them off — so on that flow this painted an empty grey bordered box above
                REQUIRED DETAILS with no label and nothing in it. SCRUM-1427 reported it
                as "an unlabelled empty field at the top of the modal", which is exactly
                what it looked like.
              */}
              {(showLocation || showFacility || showProvider || showCollectionDate) && (
              <div className="flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                {showLocation && (
                  <div>
                    <label className={labelClass}>Location</label>
                    <select className={selectClass} style={{ minWidth: 180 }} value={routingId} onChange={(e) => handleLocationChange(e.target.value)}>
                      <option value="">Select One...</option>
                      {(locations ?? []).map((l) => (
                        <option key={l.routingId} value={l.routingId}>{l.routingTitle ? `${l.routingTitle} (${l.routingId})` : l.routingId}</option>
                      ))}
                    </select>
                  </div>
                )}
                {showLocation && showPresets && !config.hidePresetsToolbar && config.presetsInLocationBar && renderPresetControls()}
                {showFacility && (
                  <div>
                    <label className={labelClass}>Facility</label>
                    <select className={selectClass} style={{ minWidth: 140 }} value={facilityId} onChange={(e) => setFacilityId(e.target.value)}>
                      <option value="">Select One...</option>
                      {(facilities ?? []).map((f) => (
                        <option key={f.accountQuestId} value={f.sendingFacility}>{f.facilityName}</option>
                      ))}
                    </select>
                  </div>
                )}
                {showProvider && (
                  <div>
                    <label className={labelClass}>Provider</label>
                    <select className={selectClass} style={{ minWidth: 140 }} value={providerId} onChange={(e) => setProviderId(e.target.value)}>
                      <option value="">Select One...</option>
                      {(providers ?? []).map((p) => (
                        <option key={p.accountQuestProviderId} value={p.providerId}>{p.providerName}</option>
                      ))}
                    </select>
                    {!setupMessages && showLocation && !!routingId && !providersFetching && (providers?.length ?? 0) === 0 && (
                      <p className="mt-1 text-xs text-slate-500">No providers are configured for this location.</p>
                    )}
                  </div>
                )}
                {showFacility && (
                  <>
                    <div>
                      <label className={labelClass}>Business Unit</label>
                      <select className={selectClass} style={{ minWidth: 80 }} value={businessUnit} onChange={(e) => setBusinessUnit(e.target.value)}>
                        {BUSINESS_UNITS.map((bu) => (
                          <option key={bu} value={bu}>{bu}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2 pb-1">
                      <input type="checkbox" id="labCollect" checked={labCollect} onChange={(e) => setLabCollect(e.target.checked)} className="rounded border-slate-300 text-blue-600" />
                      <label htmlFor="labCollect" className="text-xs font-medium text-slate-600">Lab Collect</label>
                    </div>
                  </>
                )}
                {showCollectionDate && (
                  <div>
                    <label className={labelClass}>{showCollectionTime ? 'Collection Date & Time' : 'Collection Date'}</label>
                    <div className="flex items-center gap-1">
                      <DatePicker
                        size="sm"
                        ariaLabel="Collection date"
                        value={collectionDate}
                        onChange={setCollectionDate}
                        wrapperClassName="w-40"
                      />
                      {showCollectionTime && (
                        <TimeField
                          ariaLabel="Collection time"
                          value={collectionTime}
                          onChange={setCollectionTime}
                        />
                      )}
                      {selectedTests.length > 0 && (
                        <button
                          type="button"
                          className="text-xs text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                          onClick={() => setAllCollectionDates(collectionDate)}
                          title="Apply this collection date to all selected tests"
                        >
                          Apply to all
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              )}

              {(() => {
              const detailsSection = (
              <CollapsibleSection title="Required Details" sectionKey="details" expanded={expandedSections.has('details')} onToggle={() => toggleSection('details')}>
                <div className="grid grid-cols-6 gap-3 max-md:grid-cols-1 max-md:[&>*]:col-span-1">
                  <div className="col-span-2">
                    <label className={labelClass}>First Name{reqMark}</label>
                    <input className={reqInput(!firstName.trim()) + ' w-full' + fieldLockCls('firstName')} value={firstName} onChange={(e) => setFirstName(e.target.value)} readOnly={fieldLocked('firstName')} />
                  </div>
                  <div className="col-span-1">
                    <label className={labelClass}>Middle Name/Initial</label>
                    <input className={inputClass + ' w-full' + fieldLockCls('middleName')} value={middleName} onChange={(e) => setMiddleName(e.target.value)} readOnly={fieldLocked('middleName')} />
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Last Name{reqMark}</label>
                    <input className={reqInput(!lastName.trim()) + ' w-full' + fieldLockCls('lastName')} value={lastName} onChange={(e) => setLastName(e.target.value)} readOnly={fieldLocked('lastName')} />
                  </div>
                  <div className="col-span-1 max-md:hidden" />
                </div>
                <div className="grid grid-cols-6 gap-3 max-md:grid-cols-1 max-md:[&>*]:col-span-1 mt-3">
                  <div className="col-span-2">
                    <label className={labelClass}>Date of Birth (MM/DD/YYYY){reqMark}</label>
                    <div className="flex items-center gap-1">
                      <input aria-label="Birth month" className={reqInput(!birthMonth || dobBad) + dobLockCls} style={{ width: 40 }} value={birthMonth} onChange={(e) => setBirthMonth(e.target.value)} maxLength={2} placeholder="MM" readOnly={dobLocked} />
                      <span>/</span>
                      <input aria-label="Birth day" className={reqInput(!birthDay || dobBad) + dobLockCls} style={{ width: 40 }} value={birthDay} onChange={(e) => setBirthDay(e.target.value)} maxLength={2} placeholder="DD" readOnly={dobLocked} />
                      <span>/</span>
                      <input aria-label="Birth year" className={reqInput(!birthYear || dobBad) + dobLockCls} style={{ width: 55 }} value={birthYear} onChange={(e) => setBirthYear(e.target.value)} maxLength={4} placeholder="YYYY" readOnly={dobLocked} />
                    </div>
                    {dobOnFileInvalid && (
                      <p className={`mt-1 text-[11px] ${dobBad ? 'font-medium text-red-600' : 'text-emerald-700'}`} role={dobBad ? 'alert' : undefined}>
                        {dobBad
                          ? (dobAge === null
                            ? 'Date of birth is missing or invalid on the chart. Enter the correct one to send this order — it will be saved to the patient’s chart.'
                            : `Date of birth makes the patient ${dobAge} years old. Correct it to send this order — it will be saved to the patient’s chart.`)
                          : 'Thanks — this date of birth will be saved to the patient’s chart when the order is sent.'}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Gender{reqMark}</label>
                    <select aria-label="Gender" className={reqInput(genderMissing, selectClass) + ' w-full' + (genderLocked ? lockedCls : '')} value={gender} onChange={(e) => setGender(e.target.value)} disabled={genderLocked}>
                      <option value="NS">Select one...</option>
                      <option value="F">Female</option>
                      <option value="M">Male</option>
                    </select>
                    {lockIdentity && genderOnFile !== 'usable' && (
                      <p className={`mt-1 text-[11px] ${genderMissing ? 'font-medium text-red-600' : 'text-emerald-700'}`} role={genderMissing ? 'alert' : undefined}>
                        {genderOnFile === 'blank'
                          ? (genderMissing
                            ? 'Gender is missing on the chart. Choose one to send this order — it will be saved to the patient’s chart.'
                            : 'Thanks — this gender will be saved to the patient’s chart when the order is sent.')
                          : orderGenderOnly
                          ? (genderMissing
                            ? 'The chart’s gender is not female or male, and the pharmacy needs one. Choose Female or Male for this order — the chart is not changed.'
                            : 'This order is sent with the gender you chose. The patient’s chart is not changed.')
                          : 'This order needs the patient’s gender as female or male, and the chart says something else. Set Female or Male on the patient’s chart, then open this order again.'}
                      </p>
                    )}
                  </div>
                  {showVitals && (
                    <>
                      <div className="col-span-1">
                        <label className={labelClass}>Height</label>
                        <div className="flex items-center gap-1">
                          <input className={inputClass} style={{ width: 30 }} value={heightFeet} onChange={(e) => setHeightFeet(e.target.value)} maxLength={1} />
                          <span>'</span>
                          <input className={inputClass} style={{ width: 35 }} value={heightInches} onChange={(e) => setHeightInches(e.target.value)} maxLength={2} />
                          <span>"</span>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <label className={labelClass}>Weight (lbs)</label>
                        <input className={inputClass} style={{ width: 60 }} value={weight} onChange={(e) => setWeight(e.target.value)} maxLength={4} />
                      </div>
                      <div className="col-span-1">
                        <label className={labelClass}>BP</label>
                        <div className="flex items-center gap-1">
                          <input className={inputClass} style={{ width: 40 }} value={bpSystolic} onChange={(e) => setBpSystolic(e.target.value)} maxLength={3} />
                          <span>/</span>
                          <input className={inputClass} style={{ width: 40 }} value={bpDiastolic} onChange={(e) => setBpDiastolic(e.target.value)} maxLength={3} />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="col-span-1 max-md:hidden" />
                </div>
                {/* Ship-to (ship-to-patient vendors): choose the patient's address
                    on file, a saved shipping address, or type a new one. This is
                    the delivery address for THIS ORDER ONLY — it is never written
                    back to the patient's profile/chart address. */}
                {addrReq && (
                  <>
                  <div className="mt-4 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-blue-800">
                    <Truck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      {config.addressWording ? (
                        <><span className="font-semibold">{config.addressWording.notice}</span>{' '}
                        Editing it here changes this order only and does <span className="font-semibold">not</span> change
                        the patient&apos;s profile address.</>
                      ) : (
                        <><span className="font-semibold">Delivery address for this order only.</span>{' '}
                        Editing it here ships this order to the new address and does <span className="font-semibold">not</span> change
                        the patient&apos;s profile address.</>
                      )}
                    </span>
                  </div>
                  <div className="grid grid-cols-6 gap-3 max-md:grid-cols-1 max-md:[&>*]:col-span-1 mt-3">
                    <div className="col-span-3">
                      <label className={labelClass}>{config.addressWording?.label ?? 'Ship to'}</label>
                      <select
                        aria-label={config.addressWording?.label ?? 'Ship to'}
                        className={selectClass + ' w-full'}
                        value={shipChoiceKey ? `choice:${shipChoiceKey}` : (shippingAddressId ?? '')}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v.startsWith('choice:')) {
                            setShippingAddressId(null);
                            setShipChoiceKey(v.slice('choice:'.length));
                            return;
                          }
                          setShipChoiceKey(null);
                          const id = Number(v);
                          const a = (savedAddresses ?? []).find((x) => x.patientShippingAddressId === id);
                          if (a) applySavedAddress(a);
                          else { setShippingAddressId(null); resetToPatientAddress(); }
                        }}
                      >
                        <option value="">Patient&apos;s address on file</option>
                        {(savedAddresses ?? []).map((a) => (
                          <option key={a.patientShippingAddressId} value={a.patientShippingAddressId}>
                            Saved: {a.recipientName} — {a.line1}{a.line2 ? `, ${a.line2}` : ''}, {a.city}, {a.state} {a.zip}
                          </option>
                        ))}
                        {(shipToChoices ?? []).map((c) => (
                          <option key={c.key} value={`choice:${c.key}`}>{c.label}</option>
                        ))}
                      </select>
                      <p className="mt-0.5 text-[11px] text-slate-400">
                        {shipChoice ? 'This address comes from the practice; it can’t be edited here.' : (config.addressWording?.editHint ?? '…or edit the fields below to ship somewhere new.')}
                      </p>
                    </div>
                    <div className="col-span-3">
                      <label className={labelClass}>Recipient (who receives it){reqMark}</label>
                      <input
                        className={reqInput(!recipientName.trim()) + ' w-full'}
                        value={recipientName}
                        onChange={(e) => { setRecipientName(e.target.value); setShippingAddressId(null); }}
                        placeholder="Defaults to the patient"
                        readOnly={!!shipChoice}
                      />
                    </div>
                  </div>
                  </>
                )}
                <div className="grid grid-cols-6 gap-3 max-md:grid-cols-1 max-md:[&>*]:col-span-1 mt-3">
                  <div className="col-span-2">
                    <label className={labelClass}>Street{addrReq && reqMark}</label>
                    {shipChoice ? (
                      <input className={inputClass + ' w-full bg-slate-50'} value={street} readOnly />
                    ) : (
                    <AddressAutocomplete
                      className={reqInput(addrReq && !street.trim()) + ' w-full'}
                      value={street}
                      onChange={(v) => { setStreet(v); setShippingAddressId(null); }}
                      placeholder="Start typing an address…"
                      onSelect={(a) => {
                        if (a.city) setCity(a.city);
                        if (a.state) setState(a.state);
                        if (a.zip) setZip(a.zip);
                        if (a.country) setCountry(a.country);
                      }}
                    />
                    )}
                  </div>
                  <div className="col-span-1">
                    <label className={labelClass}>City{addrReq && reqMark}</label>
                    <input className={reqInput(addrReq && !city.trim()) + ' w-full'} value={city} onChange={(e) => { setCity(e.target.value); setShippingAddressId(null); }} readOnly={!!shipChoice} />
                  </div>
                  <div className="col-span-1">
                    <label className={labelClass}>State{addrReq && reqMark}</label>
                    <input className={reqInput(addrReq && !state.trim())} style={{ width: 40 }} value={state} onChange={(e) => { setState(e.target.value); setShippingAddressId(null); }} maxLength={2} readOnly={!!shipChoice} />
                  </div>
                  <div className="col-span-1">
                    <label className={labelClass}>Zip{addrReq && reqMark}</label>
                    <input className={reqInput(addrReq && !zip.trim())} style={{ width: 80 }} value={zip} onChange={(e) => { setZip(e.target.value); setShippingAddressId(null); }} maxLength={15} readOnly={!!shipChoice} />
                  </div>
                  <div className="col-span-1 max-md:hidden" />
                </div>
                <div className="grid grid-cols-6 gap-3 max-md:grid-cols-1 max-md:[&>*]:col-span-1 mt-3">
                  <div className="col-span-2">
                    <label className={labelClass}>Apt / Suite / Floor (optional)</label>
                    <input className={inputClass + ' w-full'} value={street2} onChange={(e) => { setStreet2(e.target.value); if (addrReq) setShippingAddressId(null); }} placeholder="Apt 4B, Floor 3, …" readOnly={!!shipChoice} />
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Country</label>
                    <input className={inputClass + ' w-full'} value={country} onChange={(e) => setCountry(e.target.value)} readOnly={!!shipChoice} />
                  </div>
                  <div className="col-span-2 max-md:hidden" />
                </div>
                {/* Save the delivery address to the patient's address book. Sits at
                    the bottom-right of the address block; hidden once it matches a
                    saved entry (nothing new to save). */}
                {addrReq && !currentAddressIsSaved && !shipChoice && (
                  <div className="mt-3 flex justify-end">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={!canSaveAddress || saveAddressMutation.isPending}
                      onClick={() => saveAddressMutation.mutate()}
                      title="Save this delivery address for reuse on future orders"
                    >
                      <Save className="h-3.5 w-3.5 mr-1" />
                      {saveAddressMutation.isPending ? 'Saving…' : 'Save address'}
                    </Button>
                  </div>
                )}
                <div className="grid grid-cols-6 gap-3 max-md:grid-cols-1 max-md:[&>*]:col-span-1 mt-3">
                  <div className="col-span-2">
                    <label className={labelClass}>Cell Phone</label>
                    <input aria-label="Cell Phone" className={inputClass + ' w-full' + (contactLocked('cellphone') ? lockedCls : '')} value={cellphone} onChange={(e) => setCellphone(e.target.value)} readOnly={contactLocked('cellphone')} />
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>E-mail</label>
                    <input aria-label="E-mail" className={inputClass + ' w-full' + (contactLocked('email') ? lockedCls : '')} value={email} onChange={(e) => setEmail(e.target.value)} readOnly={contactLocked('email')} />
                  </div>
                  <div className="col-span-2 max-md:hidden" />
                </div>

                {/* PID Notes — hidden for integrations without lab PID notes (Axiom). */}
                {showNotes && <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-600">PID Notes</span>
                    <button type="button" onClick={() => setPidNotes((prev) => [...prev, { text: '', type: 'I' }])} className="text-blue-600 hover:text-blue-800">
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {pidNotes.map((note, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-12">Note {i + 1}:</span>
                      <input
                        className={inputClass + ' flex-1'}
                        value={note.text}
                        onChange={(e) => {
                          const next = [...pidNotes];
                          next[i] = { ...next[i], text: e.target.value };
                          setPidNotes(next);
                        }}
                        maxLength={60}
                      />
                      <select
                        className={selectClass}
                        style={{ width: 95 }}
                        value={note.type}
                        onChange={(e) => {
                          const next = [...pidNotes];
                          next[i] = { ...next[i], type: e.target.value as 'I' | 'R' };
                          setPidNotes(next);
                        }}
                      >
                        <option value="I">Instruction</option>
                        <option value="R">Report</option>
                      </select>
                      {pidNotes.length > 1 && (
                        <button type="button" onClick={() => setPidNotes((prev) => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}

                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div>
                      <label className={labelClass}>Patient Internal Comment</label>
                      <textarea
                        className={inputClass + ' w-full'}
                        rows={2}
                        value={pidInternalNote}
                        onChange={(e) => setPidInternalNote(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Patient External Comment</label>
                      <textarea
                        className={inputClass + ' w-full'}
                        rows={2}
                        value={pidExternalNote}
                        onChange={(e) => setPidExternalNote(e.target.value)}
                      />
                    </div>
                  </div>
                </div>}
              </CollapsibleSection>
              );

              // Custom items slot — rendered where Tests would be for integrations
              // that use their own line-item picker (kit types, Rx items, etc.)
              const testsSection = (
                <>
                  {!showTests && itemsSlot}

                  {/* Tests — hidden for integrations that use custom line items via itemsSlot */}
                  {showTests && <CollapsibleSection
                title="Tests"
                sectionKey="tests"
                expanded={expandedSections.has('tests')}
                onToggle={() => toggleSection('tests')}
                action={
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!expandedSections.has('tests')) toggleSection('tests');
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                }
              >
                <div className="relative">
                  <div className="flex items-center gap-1">
                    <Search className="h-4 w-4 text-slate-400" />
                    <input
                      className={inputClass + ' flex-1'}
                      value={testSearch}
                      onChange={(e) => handleTestSearch(e.target.value)}
                      onFocus={handleTestFocus}
                      placeholder="Search by test code or description..."
                    />
                    {testSearching && <Spinner className="h-4 w-4" />}
                  </div>
                  {testSearchMessage !== null && (
                    <div className="absolute left-0 right-0 mt-1 rounded-md border border-slate-200 bg-white shadow-lg z-20" data-testid="lab-test-search-message">
                      <p className="px-3 py-2 text-sm text-slate-500">{testSearchMessage}</p>
                    </div>
                  )}
                  {testSearchResults.length > 0 && (
                    <div className="absolute left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg z-20">
                      {searchMessages && testSearchCapped && (
                        <p className="px-3 py-2 text-xs text-amber-700 border-b border-slate-100">{searchMessages.capped}</p>
                      )}
                      {testSearchResults.map((test) => (
                        <button
                          key={test.testCode}
                          type="button"
                          onClick={() => addTest(test)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-100 last:border-0"
                        >
                          <span className="font-mono text-xs text-slate-500 mr-2">{test.testCode}</span>
                          <span className="text-slate-800">{testLabel(test.testCode, test.description)}</span>
                          {test.performingSite && <span className="ml-2 text-xs text-slate-400">({test.performingSite})</span>}
                          {test.specimenType && <span className="ml-1 text-xs text-slate-400">{test.specimenType}</span>}
                          {(test.location || test.routingId) && <span className="ml-2 text-xs text-slate-400">[{test.location || test.routingId}]</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {selectedTests.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {selectedTests.map((test, i) => (
                      <div key={test.testCode + i} className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs text-blue-600">{test.testCode}</span>
                              <span className="text-sm text-blue-900 font-medium">{testLabel(test.testCode, test.description)}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1">
                                <label className="text-[10px] text-blue-600">Collection Date:</label>
                                <DatePicker
                                  size="sm"
                                  ariaLabel="Collection date"
                                  value={test.collectionDate}
                                  onChange={(value) => updateTest(i, { collectionDate: value })}
                                  wrapperClassName="w-36"
                                  className="border-blue-300"
                                />
                              </div>
                              <button
                                type="button"
                                className="text-[10px] text-blue-600 hover:text-blue-800 underline"
                                onClick={() => updateTest(i, { obrNotes: [...test.obrNotes, { text: '', type: 'I' }] })}
                              >
                                + Note
                              </button>
                            </div>
                          </div>
                          <button type="button" onClick={() => removeTest(i)} className="text-red-400 hover:text-red-600 mt-1">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {test.obrNotes.length > 0 && (
                          <div className="mt-2 space-y-1 pl-1">
                            {test.obrNotes.map((note, ni) => (
                              <div key={ni} className="flex items-center gap-2">
                                <span className="text-[10px] text-blue-600 w-12">Note {ni + 1}:</span>
                                <input
                                  className="rounded border border-blue-300 px-1 py-0.5 text-xs flex-1"
                                  value={note.text}
                                  maxLength={60}
                                  onChange={(e) => {
                                    const next = [...test.obrNotes];
                                    next[ni] = { ...next[ni], text: e.target.value };
                                    updateTest(i, { obrNotes: next });
                                  }}
                                />
                                <select
                                  className="rounded border border-blue-300 px-1 py-0.5 text-xs bg-white"
                                  value={note.type}
                                  onChange={(e) => {
                                    const next = [...test.obrNotes];
                                    next[ni] = { ...next[ni], type: e.target.value as 'I' | 'R' };
                                    updateTest(i, { obrNotes: next });
                                  }}
                                >
                                  <option value="I">Instruction</option>
                                  <option value="R">Report</option>
                                </select>
                                <button
                                  type="button"
                                  className="text-red-400 hover:text-red-600"
                                  onClick={() => updateTest(i, { obrNotes: test.obrNotes.filter((_, j) => j !== ni) })}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {selectedTests.length === 0 && (
                  <p className="mt-2 text-xs text-slate-400 italic">No tests added. Search above to add tests.</p>
                )}
              </CollapsibleSection>}
                </>
              );

              return config.testsBeforeDetails ? (
                <>{testsSection}{detailsSection}</>
              ) : (
                <>{detailsSection}{testsSection}</>
              );
              })()}

              {/* Bill To */}
              {showBilling && (
                <CollapsibleSection
                  title="Bill To"
                  sectionKey="billTo"
                  expanded={expandedSections.has('billTo')}
                  onToggle={() => toggleSection('billTo')}
                  action={
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        addInsuranceEntry();
                        if (!expandedSections.has('billTo')) toggleSection('billTo');
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  }
                >
                  {insuranceEntries.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-xs text-slate-400 italic">No billing entries. Click + to add one.</p>
                      <Button size="sm" variant="secondary" className="mt-2" onClick={addInsuranceEntry}>Add Billing Entry</Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {insuranceEntries.map((entry, idx) => (
                        <InsuranceEntryForm
                          key={idx}
                          index={idx}
                          entry={entry}
                          onChange={(updates) => updateInsurance(idx, updates)}
                          onRemove={() => removeInsurance(idx)}
                          inputClass={inputClass}
                          selectClass={selectClass}
                          labelClass={labelClass}
                          searchInsurance={config.searchInsurance}
                        />
                      ))}
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {/* Diagnoses - shown when third-party billing exists */}
              {showDiagnoses && (
                <CollapsibleSection
                  title="Diagnoses"
                  sectionKey="diagnoses"
                  expanded={expandedSections.has('diagnoses')}
                  onToggle={() => toggleSection('diagnoses')}
                  action={
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!expandedSections.has('diagnoses')) toggleSection('diagnoses');
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  }
                >
                  <div className="relative">
                    <div className="flex items-center gap-1">
                      <Search className="h-4 w-4 text-slate-400" />
                      <input
                        className={inputClass + ' flex-1'}
                        value={dxSearch}
                        onChange={(e) => handleDxSearch(e.target.value)}
                        placeholder="Search ICD-10 code or description..."
                      />
                    </div>
                    {dxSearchResults.length > 0 && (
                      <div className="absolute left-0 right-0 mt-1 max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg z-20">
                        {dxSearchResults.map((dx) => (
                          <button
                            key={dx.code}
                            type="button"
                            onClick={() => addDiagnosis(dx)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-100 last:border-0"
                          >
                            <span className="font-mono text-xs text-slate-500 mr-2">{dx.code}</span>
                            <span className="text-slate-800">{dx.title}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {diagnosisEntries.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {diagnosisEntries.map((dx, i) => (
                        <div key={dx.code} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm">
                          <span><span className="font-mono text-xs text-slate-500 mr-2">{dx.code}</span>{dx.title}</span>
                          <button type="button" onClick={() => removeDiagnosis(i)} className="text-red-400 hover:text-red-600">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {showNotes && (<>
              {/* Internal Notes */}
              <CollapsibleSection
                title="Internal Notes"
                sectionKey="internal"
                expanded={expandedSections.has('internal')}
                onToggle={() => toggleSection('internal')}
                action={
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setInternalNotes((prev) => [...prev, '']);
                      if (!expandedSections.has('internal')) toggleSection('internal');
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                }
              >
                {internalNotes.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No internal notes. Click + to add.</p>
                ) : (
                  <div className="space-y-2">
                    {internalNotes.map((note, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          className={inputClass + ' flex-1'}
                          value={note}
                          onChange={(e) => {
                            const next = [...internalNotes];
                            next[i] = e.target.value;
                            setInternalNotes(next);
                          }}
                          placeholder="Internal note..."
                        />
                        <button type="button" onClick={() => setInternalNotes((prev) => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CollapsibleSection>

              {/* External Notes */}
              <CollapsibleSection
                title="External Notes"
                sectionKey="external"
                expanded={expandedSections.has('external')}
                onToggle={() => toggleSection('external')}
                action={
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExternalNotes((prev) => [...prev, '']);
                      if (!expandedSections.has('external')) toggleSection('external');
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                }
              >
                {externalNotes.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No external notes. Click + to add.</p>
                ) : (
                  <div className="space-y-2">
                    {externalNotes.map((note, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          className={inputClass + ' flex-1'}
                          value={note}
                          onChange={(e) => {
                            const next = [...externalNotes];
                            next[i] = e.target.value;
                            setExternalNotes(next);
                          }}
                          placeholder="External note..."
                        />
                        <button type="button" onClick={() => setExternalNotes((prev) => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CollapsibleSection>
              </>)}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ─── Collapsible Section wrapper ─── */
function CollapsibleSection({
  title,
  expanded,
  onToggle,
  action,
  children,
}: {
  title: string;
  sectionKey: string;
  expanded: boolean;
  onToggle: () => void;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200">
      <button
        type="button"
        onClick={onToggle}
        className={
          'flex items-center justify-between w-full px-4 py-2 bg-slate-100 hover:bg-slate-150 text-sm font-semibold text-slate-800 uppercase tracking-wide ' +
          (expanded ? 'rounded-t-lg' : 'rounded-lg')
        }
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {title}
        </div>
        {action}
      </button>
      {expanded && <div className="px-4 py-3">{children}</div>}
    </div>
  );
}

/* ─── Insurance Entry Form ─── */
function InsuranceEntryForm({
  index,
  entry,
  onChange,
  onRemove,
  inputClass,
  selectClass,
  labelClass,
  searchInsurance,
}: {
  index: number;
  entry: InsuranceEntry;
  onChange: (updates: Partial<InsuranceEntry>) => void;
  onRemove: () => void;
  inputClass: string;
  selectClass: string;
  labelClass: string;
  searchInsurance?: (query: string) => Promise<QuestInsurance[]>;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<QuestInsurance[]>([]);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleSearch(query: string) {
    setSearchQuery(query);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (query.trim().length < 2 || !searchInsurance) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const results = await searchInsurance(query.trim());
        setSearchResults(results);
      } catch { setSearchResults([]); }
    }, 300);
  }

  const isThirdParty = entry.billingType === 'T';
  const showGuarantor = entry.billingType === 'T' || entry.billingType === 'P';

  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-3 relative">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-600">Billing Entry #{index + 1}</span>
        <button type="button" onClick={onRemove} className="text-red-400 hover:text-red-600">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div>
          <label className={labelClass}>Bill Type</label>
          <select className={selectClass + ' w-full'} value={entry.billingType} onChange={(e) => onChange({ billingType: e.target.value })}>
            {BILLING_TYPES.map((bt) => (
              <option key={bt.value} value={bt.value}>{bt.label}</option>
            ))}
          </select>
        </div>

        {isThirdParty && (
          <>
            <div className="col-span-2 relative">
              <label className={labelClass}>Insurance Provider</label>
              <input
                className={inputClass + ' w-full'}
                value={entry.insuranceName ? entry.insuranceName : searchQuery}
                onChange={(e) => {
                  if (entry.insuranceName) onChange({ insuranceName: '', insuranceId: '' });
                  handleSearch(e.target.value);
                }}
                placeholder="Search insurance..."
              />
              {searchResults.length > 0 && (
                <div className="absolute left-0 right-0 mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg z-20">
                  {searchResults.map((ins) => (
                    <button
                      key={ins.value}
                      type="button"
                      onClick={() => {
                        onChange({
                          insuranceId: ins.value,
                          insuranceName: ins.name,
                          coStreet: ins.address || entry.coStreet,
                          coCity: ins.city || entry.coCity,
                          coState: ins.state || entry.coState,
                          coZip: ins.zip || entry.coZip,
                        });
                        setSearchQuery('');
                        setSearchResults([]);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-100 last:border-0"
                    >
                      {ins.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className={labelClass}>Relationship</label>
              <select className={selectClass + ' w-full'} value={entry.relationship} onChange={(e) => onChange({ relationship: e.target.value })}>
                <option value="">Select one...</option>
                <option value="1">Self</option>
                <option value="2">Spouse</option>
                <option value="8">Dependent</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Policy #</label>
              <input className={inputClass + ' w-full'} value={entry.policyNumber} onChange={(e) => onChange({ policyNumber: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Group #</label>
              <input className={inputClass + ' w-full'} value={entry.groupNumber} onChange={(e) => onChange({ groupNumber: e.target.value })} />
            </div>
          </>
        )}
      </div>

      {isThirdParty && (
        <div>
          <h4 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Insurance Company Address</h4>
          <div className="grid grid-cols-4 gap-2">
            <div className="col-span-2"><label className={labelClass}>Street</label><input className={inputClass + ' w-full'} value={entry.coStreet} onChange={(e) => onChange({ coStreet: e.target.value })} /></div>
            <div><label className={labelClass}>City</label><input className={inputClass + ' w-full'} value={entry.coCity} onChange={(e) => onChange({ coCity: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className={labelClass}>State</label><input className={inputClass + ' w-full'} value={entry.coState} onChange={(e) => onChange({ coState: e.target.value })} maxLength={2} /></div>
              <div><label className={labelClass}>Zip</label><input className={inputClass + ' w-full'} value={entry.coZip} onChange={(e) => onChange({ coZip: e.target.value })} /></div>
            </div>
          </div>
        </div>
      )}

      {showGuarantor && (
        <div>
          <h4 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Guarantor</h4>
          <div className="grid grid-cols-4 gap-2">
            <div><label className={labelClass}>First</label><input className={inputClass + ' w-full'} value={entry.guarantorFirstName} onChange={(e) => onChange({ guarantorFirstName: e.target.value })} /></div>
            <div><label className={labelClass}>Middle</label><input className={inputClass + ' w-full'} value={entry.guarantorMiddleName} onChange={(e) => onChange({ guarantorMiddleName: e.target.value })} /></div>
            <div><label className={labelClass}>Last</label><input className={inputClass + ' w-full'} value={entry.guarantorLastName} onChange={(e) => onChange({ guarantorLastName: e.target.value })} /></div>
            <div><label className={labelClass}>Phone</label><input className={inputClass + ' w-full'} value={entry.guarantorPhone} onChange={(e) => onChange({ guarantorPhone: e.target.value })} /></div>
            <div><label className={labelClass}>Street</label><input className={inputClass + ' w-full'} value={entry.guarantorAddress} onChange={(e) => onChange({ guarantorAddress: e.target.value })} /></div>
            <div><label className={labelClass}>City</label><input className={inputClass + ' w-full'} value={entry.guarantorCity} onChange={(e) => onChange({ guarantorCity: e.target.value })} /></div>
            <div><label className={labelClass}>State</label><input className={inputClass + ' w-full'} value={entry.guarantorState} onChange={(e) => onChange({ guarantorState: e.target.value })} maxLength={2} /></div>
            <div><label className={labelClass}>Zip</label><input className={inputClass + ' w-full'} value={entry.guarantorZip} onChange={(e) => onChange({ guarantorZip: e.target.value })} /></div>
          </div>
        </div>
      )}

      {isThirdParty && (
          <div>
            <h4 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Insured</h4>
            <div className="grid grid-cols-4 gap-2">
              <div><label className={labelClass}>First</label><input className={inputClass + ' w-full'} value={entry.insuredFirstName} onChange={(e) => onChange({ insuredFirstName: e.target.value })} /></div>
              <div><label className={labelClass}>Middle</label><input className={inputClass + ' w-full'} value={entry.insuredMiddleName} onChange={(e) => onChange({ insuredMiddleName: e.target.value })} /></div>
              <div><label className={labelClass}>Last</label><input className={inputClass + ' w-full'} value={entry.insuredLastName} onChange={(e) => onChange({ insuredLastName: e.target.value })} /></div>
              <div />
              <div><label className={labelClass}>Street</label><input className={inputClass + ' w-full'} value={entry.insuredAddress} onChange={(e) => onChange({ insuredAddress: e.target.value })} /></div>
              <div><label className={labelClass}>City</label><input className={inputClass + ' w-full'} value={entry.insuredCity} onChange={(e) => onChange({ insuredCity: e.target.value })} /></div>
              <div><label className={labelClass}>State</label><input className={inputClass + ' w-full'} value={entry.insuredState} onChange={(e) => onChange({ insuredState: e.target.value })} maxLength={2} /></div>
              <div><label className={labelClass}>Zip</label><input className={inputClass + ' w-full'} value={entry.insuredZip} onChange={(e) => onChange({ insuredZip: e.target.value })} /></div>
            </div>
          </div>
      )}
    </div>
  );
}

/* ─── Confirmation View ─── */
function ConfirmationView({
  showFacility,
  showProvider,
  showBilling,
  showTests,
  confirmSlot,
  facilityName,
  providerName,
  businessUnit,
  collectionDate,
  collectionTime,
  labCollect,
  firstName,
  lastName,
  birthMonth,
  birthDay,
  birthYear,
  gender,
  tests,
  insuranceEntries,
  diagnoses,
  onBack,
  onSubmit,
  submitting,
}: {
  showFacility: boolean;
  showProvider: boolean;
  showBilling: boolean;
  showTests: boolean;
  confirmSlot?: React.ReactNode;
  facilityName: string;
  providerName: string;
  businessUnit: string;
  collectionDate: string;
  /** 'HH:MM' (24h), or '' when the vendor takes no collection time. */
  collectionTime: string;
  labCollect: boolean;
  firstName: string;
  lastName: string;
  birthMonth: string;
  birthDay: string;
  birthYear: string;
  gender: string;
  tests: SelectedTest[];
  insuranceEntries: InsuranceEntry[];
  diagnoses: QuestDiagnosis[];
  onBack: () => void;
  onSubmit: () => void;
  submitting: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm space-y-3">
        <h3 className="font-semibold text-blue-900 text-base">Order Confirmation</h3>

        <div className="grid grid-cols-2 gap-2 text-blue-800">
          {showFacility && <p><strong>Facility:</strong> {facilityName}</p>}
          {showProvider && <p><strong>Provider:</strong> {providerName}</p>}
          {showFacility && <p><strong>Business Unit:</strong> {businessUnit}</p>}
          {collectionTime
            // The dates each test is actually sent with: a test keeps its own
            // date unless "Apply to all" copied the order-level one onto it.
            ? <p><strong>Collection:</strong> {sentCollectionDates(tests, collectionDate)} {formatTime12h(collectionTime)}</p>
            : <p><strong>Collection Date:</strong> {collectionDate}</p>}
          {showFacility && labCollect && <p><strong>Lab Collect:</strong> Yes</p>}
        </div>

        <div className="border-t border-blue-200 pt-2">
          <p className="text-blue-800"><strong>Patient:</strong> {lastName}, {firstName}</p>
          <p className="text-blue-800"><strong>DOB:</strong> {birthMonth}/{birthDay}/{birthYear}</p>
          <p className="text-blue-800"><strong>Gender:</strong> {gender === 'F' ? 'Female' : gender === 'M' ? 'Male' : 'N/A'}</p>
        </div>

        {/* Tests list — shown for Quest/Vibrant/GetLabs; replaced by confirmSlot
            for integrations with custom line items (SiPhox, Axiom, etc.) */}
        {showTests ? (
          <div className="border-t border-blue-200 pt-2">
            <p className="font-semibold text-blue-900">Tests ({tests.length}):</p>
            <ul className="list-disc ml-5 space-y-1">
              {tests.map((t, i) => (
                <li key={i} className="text-blue-800">
                  <span className="font-mono text-xs">{t.testCode}</span> — {t.description}
                  {t.obrNotes.filter((n) => n.text.trim()).map((n, ni) => (
                    <span key={ni} className="italic text-xs"> ({n.type}: {n.text})</span>
                  ))}
                </li>
              ))}
            </ul>
          </div>
        ) : confirmSlot ? (
          <div className="border-t border-blue-200 pt-2">{confirmSlot}</div>
        ) : null}

        {showBilling && insuranceEntries.length > 0 && (
          <div className="border-t border-blue-200 pt-2">
            <p className="font-semibold text-blue-900">Billing:</p>
            {insuranceEntries.map((e, i) => (
              <p key={i} className="text-blue-800">
                {BILLING_TYPES.find((bt) => bt.value === e.billingType)?.label ?? e.billingType}
                {e.insuranceName ? ` — ${e.insuranceName}` : ''}
                {e.policyNumber ? ` (Policy: ${e.policyNumber})` : ''}
              </p>
            ))}
          </div>
        )}

        {showBilling && diagnoses.length > 0 && (
          <div className="border-t border-blue-200 pt-2">
            <p className="font-semibold text-blue-900">Diagnoses:</p>
            <ul className="list-disc ml-5">
              {diagnoses.map((dx) => (
                <li key={dx.code} className="text-blue-800">{dx.code} — {dx.title}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3">
        <Button variant="secondary" onClick={onBack} disabled={submitting}>Back</Button>
        <Button onClick={onSubmit} loading={submitting}>Confirm &amp; Submit Order</Button>
      </div>
    </div>
  );
}
